import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Appraisal Maker - ENHANCED MOCK VERSION
 *
 * Professional jewelry appraisal creation tool for finished jewelry.
 * This is an enhanced mock/prototype to demonstrate full capabilities to the client.
 *
 * Enhanced Features:
 * - Statistics dashboard
 * - Progress percentage on wizard
 * - Editable gemstones with inline editing
 * - Auto-calculate total value
 * - Template selector (Classic, Modern, Detailed)
 * - Digital signature pad
 * - QR code generation
 * - Photos in PDF preview
 * - Email preview
 * - Form validation
 * - Save draft & duplicate features
 * - 15 mock appraisals with variety
 * - Filter by date range, status, customer
 * - Sort by multiple criteria
 * - Export to CSV
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { AppraisalPDFDocument } from './AppraisalPDFDocument';
import { urlToBase64 } from '../../utils/imageUtils';
import { PageContainer, PageTitle, SectionTitle, Text, Button, Input, Label, FormGroup, Badge } from '../../components/PageStyles';
import { BaseModal } from '../../components/shared/BaseModal';
import { apiClient } from '../../utils/apiClient';
import { notify } from '../../utils/notifications';
import { useAuthStore } from '../../stores/authStore';
import { FaGem, FaFileAlt, FaImage, FaDownload, FaEye, FaPlus, FaEdit, FaTrash, FaCheckCircle, FaRing, FaLink, FaBolt, FaCopy, FaSave, FaEnvelope, FaFileCsv, FaChartLine, FaDollarSign, FaCalendarAlt, FaUser } from 'react-icons/fa';
// Styled Components
const StatsOverview = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;
const StatCard = styled.div `
  background: var(--bg-secondary);
  border: 2px solid ${props => props.$color || 'var(--border-color)'};
  padding: 24px;
  text-align: center;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  .stat-icon {
    font-size: 32px;
    color: ${props => props.$color || 'var(--text-primary)'};
    margin-bottom: 12px;
  }

  .stat-value {
    font-size: 36px;
    font-weight: bold;
    color: var(--text-primary);
    margin-bottom: 4px;
  }

  .stat-label {
    font-size: 11px;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;
const HeaderSection = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
`;
const CustomFieldsSection = styled.div `
  margin-top: 32px;
  padding: 20px;
  background: var(--bg-tertiary);
  border: 2px dashed var(--border-color);
`;
const CustomFieldItem = styled.div `
  display: grid;
  grid-template-columns: 1fr 2fr auto;
  gap: 12px;
  margin-bottom: 12px;
  align-items: center;
`;
const CustomFieldInput = styled.input `
  padding: 10px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 13px;

  &::placeholder {
    color: var(--text-tertiary);
  }

  &:focus {
    outline: none;
    border-color: var(--accent-color);
  }
`;
const ModeSelector = styled.div `
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  padding: 16px;
  background: var(--bg-secondary);
  border: 2px solid var(--border-color);
  align-items: center;
`;
const ModeButton = styled.button `
  padding: 10px 20px;
  background: ${props => props.$active ? 'var(--text-primary)' : 'transparent'};
  color: ${props => props.$active ? 'var(--bg-primary)' : 'var(--text-secondary)'};
  border: 2px solid ${props => props.$active ? 'var(--text-primary)' : 'var(--border-color)'};
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--text-primary);
    color: ${props => props.$active ? 'var(--bg-primary)' : 'var(--text-primary)'};
  }

  svg {
    margin-right: 6px;
  }
`;
const ModeLabel = styled.div `
  margin-right: 16px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 1px;
`;
const TabBar = styled.div `
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
  border-bottom: 2px solid var(--border-color);
`;
const Tab = styled.button `
  background: none;
  border: none;
  padding: 12px 24px;
  color: ${props => props.$active ? 'var(--text-primary)' : 'var(--text-secondary)'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border-bottom: 3px solid ${props => props.$active ? 'var(--accent-color)' : 'transparent'};
  margin-bottom: -2px;
  transition: all 0.2s;

  &:hover {
    color: var(--text-primary);
  }

  svg {
    margin-right: 8px;
  }
`;
const WizardContainer = styled.div `
  background: var(--bg-secondary);
  border: 2px solid var(--border-color);
  padding: 40px;
  margin-bottom: 32px;
  transition: all 0.3s ease;
`;
const WizardSteps = styled.div `
  display: flex;
  justify-content: space-between;
  margin-bottom: 48px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 20px;
    left: 10%;
    right: 10%;
    height: 2px;
    background: var(--border-color);
    z-index: 0;
  }
`;
const ProgressBar = styled.div `
  position: absolute;
  top: 20px;
  left: 10%;
  height: 2px;
  width: ${props => props.$progress}%;
  background: linear-gradient(90deg, #10b981, #3b82f6);
  z-index: 1;
  transition: width 0.5s ease;
`;
const WizardStep = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
  position: relative;
  z-index: 2;

  .step-circle {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${props => props.$completed ? '#10b981' : props.$active ? 'var(--accent-color)' : 'var(--bg-secondary)'};
    border: 2px solid ${props => props.$completed ? '#10b981' : props.$active ? 'var(--accent-color)' : 'var(--border-color)'};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.$completed || props.$active ? 'var(--bg-primary)' : 'var(--text-tertiary)'};
    font-weight: bold;
    font-size: 16px;
    transition: all 0.3s ease;
  }

  .step-label {
    font-size: 12px;
    color: ${props => props.$active ? 'var(--text-primary)' : 'var(--text-tertiary)'};
    font-weight: ${props => props.$active ? '600' : '400'};
    text-align: center;
  }
`;
const ProgressIndicator = styled.div `
  text-align: center;
  margin-bottom: 24px;
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 600;

  .percentage {
    font-size: 24px;
    color: #10b981;
    margin-bottom: 4px;
  }
`;
const FormGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 24px;
`;
const Select = styled.select `
  width: 100%;
  padding: 12px;
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 2px solid var(--border-color);
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-color);
  }

  &.error {
    border-color: #ef4444;
  }
`;
const Textarea = styled.textarea `
  width: 100%;
  min-height: 120px;
  padding: 12px;
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 2px solid var(--border-color);
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-color);
  }
`;
const ImageUploadArea = styled.div `
  border: 2px dashed var(--border-color);
  padding: 40px;
  text-align: center;
  background: var(--bg-primary);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent-color);
    background: var(--bg-tertiary);
  }

  svg {
    font-size: 48px;
    color: var(--text-tertiary);
    margin-bottom: 16px;
  }
`;
const ImagePreviewGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
  margin-top: 24px;
`;
const ImagePreview = styled.div `
  position: relative;
  aspect-ratio: 1;
  border: 2px solid var(--border-color);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .remove-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(239, 68, 68, 0.9);
    color: white;
    border: none;
    width: 30px;
    height: 30px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    transition: background 0.2s;

    &:hover {
      background: #dc2626;
    }
  }
`;
const ButtonGroup = styled.div `
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-top: 32px;
  padding-top: 32px;
  border-top: 2px solid var(--border-color);
`;
const AppraisalsList = styled.div `
  display: grid;
  gap: 16px;
`;
const AppraisalCard = styled.div `
  background: var(--bg-secondary);
  border: 2px solid var(--border-color);
  padding: 24px;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent-color);
    transform: translateY(-2px);
  }

  .appraisal-header {
    display: flex;
    justify-content: space-between;
    align-items: start;
    margin-bottom: 16px;

    h3 {
      color: var(--text-primary);
      font-size: 18px;
      margin: 0 0 8px 0;
      display: flex;
      align-items: center;
      gap: 12px;

      svg {
        color: var(--accent-color);
      }
    }

    .customer-name {
      font-size: 14px;
      color: var(--text-secondary);
    }
  }

  .appraisal-details {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 16px;

    .detail-item {
      .label {
        font-size: 11px;
        color: var(--text-tertiary);
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 4px;
      }

      .value {
        font-size: 14px;
        color: var(--text-primary);
        font-weight: 600;
      }
    }
  }

  .appraisal-actions {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;

    button {
      font-size: 13px;
      padding: 8px 16px;
    }
  }
`;
const PDFPreview = styled.div `
  background: white;
  color: black;
  padding: 60px;
  max-width: 800px;
  margin: 0 auto;
  font-family: 'Times New Roman', serif;
  line-height: 1.6;
  word-spacing: normal;
  letter-spacing: normal;

  .pdf-header {
    text-align: center;
    margin-bottom: 40px;
    padding-bottom: 20px;
    border-bottom: 3px solid #000;

    h1 {
      font-size: 28px;
      margin: 0 0 8px 0;
      color: #000;
    }

    .company-name {
      font-size: 20px;
      font-weight: bold;
      color: #1a1a1a;
      margin-bottom: 4px;
    }

    .company-info {
      font-size: 12px;
      color: #666;
    }
  }

  .pdf-section {
    margin-bottom: 32px;
    page-break-inside: avoid;
    break-inside: avoid;

    h2 {
      font-size: 18px;
      margin: 0 0 16px 0;
      padding-bottom: 8px;
      border-bottom: 2px solid #ddd;
      color: #000;
    }

    .info-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
      font-size: 14px;

      .info-row {
        display: flex;
        gap: 8px;

        .label {
          font-weight: bold;
          color: #333;
        }

        .value {
          color: #000;
        }
      }
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 16px;
      page-break-inside: avoid;
      break-inside: avoid;

      th, td {
        border: 1px solid #ddd;
        padding: 10px;
        text-align: left;
        font-size: 13px;
      }

      th {
        background: #f5f5f5;
        font-weight: bold;
        color: #000;
      }

      td {
        color: #000;
      }
    }
  }

  .pdf-photos {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-top: 16px;
    page-break-inside: avoid;
    break-inside: avoid;

    img {
      width: 100%;
      height: 200px;
      object-fit: contain;
      border: 1px solid #ddd;
      background: #f9f9f9;
    }
  }

  .pdf-total {
    text-align: right;
    font-size: 24px;
    font-weight: bold;
    margin-top: 40px;
    padding-top: 20px;
    border-top: 3px solid #000;
    color: #000;
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .pdf-signature {
    display: flex;
    justify-content: space-between;
    margin-top: 40px;
    padding-top: 20px;
    border-top: 2px solid #ddd;
    page-break-inside: avoid;
    break-inside: avoid;

    .signature-box {
      width: 45%;

      .signature-label {
        font-size: 12px;
        color: #666;
        margin-bottom: 8px;
      }

      .signature-line {
        border-bottom: 1px solid #000;
        height: 60px;
        margin-bottom: 8px;
      }

      .signature-name {
        font-size: 11px;
        color: #333;
      }
    }
  }

  .pdf-qr {
    text-align: center;
    margin-top: 20px;
    padding: 16px;
    background: #f9f9f9;
    border: 1px solid #ddd;

    .qr-code {
      width: 100px;
      height: 100px;
      background: white;
      border: 2px solid #000;
      margin: 0 auto 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 40px;
    }

    .qr-text {
      font-size: 10px;
      color: #666;
    }
  }

`;
const GemstoneList = styled.div `
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;
const GemstoneItem = styled.div `
  background: ${props => props.$editing ? 'var(--bg-primary)' : 'var(--bg-tertiary)'};
  border: 2px solid ${props => props.$editing ? 'var(--accent-color)' : 'var(--border-color)'};
  padding: 20px;
  transition: all 0.2s;

  .gemstone-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    h4 {
      margin: 0;
      color: var(--text-primary);
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 8px;

      svg {
        color: #10b981;
      }
    }

    .actions {
      display: flex;
      gap: 8px;
    }
  }

  .gemstone-details {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 12px;

    .detail {
      .label {
        font-size: 11px;
        color: var(--text-tertiary);
        text-transform: uppercase;
        margin-bottom: 4px;
      }

      .value {
        font-size: 14px;
        color: var(--text-primary);
        font-weight: 600;
      }

      input {
        width: 100%;
        padding: 8px;
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        color: var(--text-primary);
        font-size: 14px;

        &:focus {
          outline: none;
          border-color: var(--accent-color);
        }
      }

      select {
        width: 100%;
        padding: 8px;
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        color: var(--text-primary);
        font-size: 14px;
        cursor: pointer;

        &:focus {
          outline: none;
          border-color: var(--accent-color);
        }
      }
    }
  }
`;
const FilterBar = styled.div `
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  align-items: center;
`;
const SummaryCard = styled.div `
  background: var(--bg-tertiary);
  border: 2px solid var(--border-color);
  padding: 24px;
  margin-bottom: 24px;

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;

    .summary-item {
      .label {
        font-size: 11px;
        color: var(--text-tertiary);
        text-transform: uppercase;
        margin-bottom: 4px;
      }

      .value {
        font-size: 16px;
        color: var(--text-primary);
        font-weight: 600;
      }
    }
  }
`;
const ValidationMessage = styled.div `
  background: rgba(239, 68, 68, 0.1);
  border: 2px solid #ef4444;
  padding: 12px 16px;
  margin-top: 16px;
  color: #ef4444;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const TemplateSelector = styled.div `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 32px;
`;
const TemplateCard = styled.div `
  border: 2px solid ${props => props.$selected ? 'var(--accent-color)' : 'var(--border-color)'};
  padding: 20px;
  text-align: center;
  cursor: pointer;
  background: ${props => props.$selected ? 'var(--bg-tertiary)' : 'var(--bg-secondary)'};
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent-color);
  }

  .template-icon {
    font-size: 32px;
    margin-bottom: 12px;
    color: ${props => props.$selected ? 'var(--accent-color)' : 'var(--text-secondary)'};
  }

  .template-name {
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 4px;
  }

  .template-desc {
    font-size: 12px;
    color: var(--text-secondary);
  }
`;
const SignaturePad = styled.div `
  border: 2px solid var(--border-color);
  padding: 20px;
  text-align: center;
  background: white;
  margin-bottom: 16px;

  .sig-canvas {
    width: 100%;
    height: 150px;
    border: 1px dashed #ccc;
    background: #fafafa;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
    font-style: italic;
    cursor: pointer;

    &:hover {
      background: #f0f0f0;
    }
  }

  .sig-actions {
    margin-top: 12px;
    display: flex;
    gap: 8px;
    justify-content: center;
  }
`;
// Constants
const DUAL_SIGNATURE_IMAGE_URL = 'https://blobaneesh.blob.core.windows.net/images/appraisals/sig.bmp';
const DIAMOND_LOGO_URL = 'https://blobaneesh.blob.core.windows.net/images/appraisals/diamondvector.png';
const AppraisalMaker = () => {
    const { accessToken, user } = useAuthStore();
    const userRoleLevel = user?.roleLevel || 0;
    const isAdmin = userRoleLevel >= 7; // SALES_MANAGER and above
    const [mode] = useState('test'); // Test mode for real appraisals
    const [activeTab, setActiveTab] = useState('create');
    const [currentStep, setCurrentStep] = useState(1);
    const [showPDFPreview, setShowPDFPreview] = useState(false);
    const [showEmailPreview, setShowEmailPreview] = useState(false);
    const [selectedAppraisal, setSelectedAppraisal] = useState(null);
    const [editingGemId, setEditingGemId] = useState(null);
    const [signatureDrawn, setSignatureDrawn] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [editingAppraisalId, setEditingAppraisalId] = useState(null);
    const [editingAppraisalNumber, setEditingAppraisalNumber] = useState(null);
    // Form state
    const [formData, setFormData] = useState({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        customerAddress: '',
        jewelryType: '',
        metalType: '',
        metalPurity: '',
        metalWeight: '',
        settingDescription: '',
        condition: 'excellent',
        totalValue: '',
        template: 'classic',
        // Test mode specific fields
        itemDescription: '',
        diamondRingValue: '',
        goldRingValue: '',
        goldPriceReference: '',
        appraiser1Name: 'Aneesh K. Jain',
        appraiser1Title: 'GIA, Graduate Diamonds',
        appraiser2Name: 'Michael Gold',
        appraiser2Title: 'GIA, Graduate Gemologist',
        companyName: 'Aneesh Jain, LLC.',
        companyAddress: '525 Vine St, 23rd floor\nCincinnati, OH 45202',
        companyPhone: '513-349-8816',
        showRoyalGemsBranding: false // Toggle for personal vs business appraisals
    });
    const [gemstones, setGemstones] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [downloadingPDF, setDownloadingPDF] = useState(false);
    const fileInputRef = useRef(null);
    const pdfContentRef = useRef(null);
    // Custom fields for each step (flexible key-value pairs with stable IDs)
    const [customFieldsStep1, setCustomFieldsStep1] = useState([]);
    const [customFieldsStep2, setCustomFieldsStep2] = useState([]);
    const [customFieldsStep5, setCustomFieldsStep5] = useState([]);
    // Templates and real appraisals state
    const [templates, setTemplates] = useState([]);
    const [realAppraisals, setRealAppraisals] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [loadingAppraisals, setLoadingAppraisals] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [templateDescription, setTemplateDescription] = useState('');
    // Filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('all');
    const [sortBy, setSortBy] = useState('date');
    // Load templates and appraisals on mount
    useEffect(() => {
        if (mode === 'test') {
            loadTemplates();
            loadAppraisals();
        }
    }, [mode]);
    // Auto-switch to history tab when RG Appraisal tour needs it
    useEffect(() => {
        const handleSwitchToHistoryTab = () => {
            console.log('📬 Received switchToHistoryTabForTour event - switching to history tab');
            setActiveTab('history');
        };
        // Listen for custom event from tour context
        window.addEventListener('switchToHistoryTabForTour', handleSwitchToHistoryTab);
        return () => {
            window.removeEventListener('switchToHistoryTabForTour', handleSwitchToHistoryTab);
        };
    }, []);
    // API Functions
    const loadTemplates = async () => {
        try {
            setLoadingTemplates(true);
            const response = await apiClient.get('appraisals-get-templates');
            if (response.ok && response.data?.success) {
                setTemplates(response.data.templates);
            }
        }
        catch (error) {
            console.error('Error loading templates:', error);
        }
        finally {
            setLoadingTemplates(false);
        }
    };
    const loadAppraisals = async () => {
        try {
            setLoadingAppraisals(true);
            const response = await apiClient.get('appraisals-get?status=all');
            console.log('📥 Appraisals API Response:', {
                success: response.ok,
                debug: response.data?.debug,
                count: response.data?.appraisals?.length,
                total: response.data?.total
            });
            if (response.ok && response.data?.success) {
                console.log('✅ Loaded appraisals:', response.data.appraisals);
                setRealAppraisals(response.data.appraisals);
            }
        }
        catch (error) {
            console.error('Error loading appraisals:', error);
        }
        finally {
            setLoadingAppraisals(false);
        }
    };
    const saveTemplate = async () => {
        if (!templateName.trim()) {
            alert('Please enter a template name');
            return;
        }
        try {
            console.log('💾 Saving template:', templateName);
            const response = await apiClient.post('appraisals-save-template', {
                templateName,
                description: templateDescription,
                category: formData.jewelryType.toLowerCase().replace(/\s+/g, '-') || 'custom',
                formData,
                customFieldsStep1,
                customFieldsStep2,
                customFieldsStep5,
                gemstoneTemplates: gemstones
            });
            console.log('📥 Save template response:', response);
            console.log('📊 Response.ok:', response.ok);
            console.log('📊 Response.data:', response.data);
            if (response.ok && response.data?.success) {
                alert(`✅ Template "${templateName}" saved successfully!`);
                setShowSaveTemplateModal(false);
                setTemplateName('');
                setTemplateDescription('');
                loadTemplates();
            }
            else {
                console.error('❌ Save failed - response:', response);
                alert(`❌ Failed to save template: ${response.data?.error || response.error || 'Unknown error'}`);
            }
        }
        catch (error) {
            console.error('Error saving template:', error);
            alert('❌ Error saving template');
        }
    };
    const loadTemplate = async (templateId) => {
        const template = templates.find(t => t.id === templateId);
        if (!template)
            return;
        // Load template data into form
        setFormData({ ...formData, ...template.formData });
        setGemstones(template.gemstoneTemplates || []);
        setCustomFieldsStep1(template.customFieldsStep1 || []);
        setCustomFieldsStep2(template.customFieldsStep2 || []);
        setCustomFieldsStep5(template.customFieldsStep5 || []);
        setCurrentStep(1);
        alert(`✅ Template "${template.templateName}" loaded!`);
    };
    const saveAppraisal = async (status) => {
        try {
            const response = await apiClient.post('appraisals-save', {
                id: editingAppraisalId || undefined, // Include ID if editing
                appraisalNumber: editingAppraisalNumber || undefined, // Preserve appraisal number when editing
                status,
                customerName: formData.customerName,
                customerEmail: formData.customerEmail,
                customerAddress: formData.customerAddress,
                templateUsed: selectedTemplateId || null,
                formData,
                customFieldsStep1,
                customFieldsStep2,
                customFieldsStep5,
                gemstones,
                photos,
                signatureData: mode === 'test'
                    ? DUAL_SIGNATURE_IMAGE_URL
                    : (signatureDrawn ? 'signature-data' : null)
            });
            if (response.ok && response.data?.success) {
                const appraisal = response.data.appraisal;
                const action = editingAppraisalId ? 'updated' : (status === 'draft' ? 'saved as draft' : 'completed');
                alert(`✅ Appraisal ${appraisal.appraisalNumber} ${action}!`);
                // Clear editing state
                setEditingAppraisalId(null);
                setEditingAppraisalNumber(null);
                loadAppraisals();
                if (status === 'completed') {
                    setActiveTab('history');
                }
            }
            else {
                alert('❌ Failed to save appraisal');
            }
        }
        catch (error) {
            console.error('Error saving appraisal:', error);
            alert('❌ Error saving appraisal');
        }
    };
    // Enhanced mock data - 15 appraisals
    const [mockAppraisals] = useState([
        {
            id: 'APR-2025-001',
            customerName: 'Sarah Johnson',
            customerEmail: 'sarah.j@email.com',
            customerPhone: '(555) 123-4567',
            jewelryType: 'Engagement Ring',
            metalType: 'Platinum',
            metalPurity: '950 Platinum',
            metalWeight: '4.2',
            gemstones: [
                { id: '1', species: 'Diamond', cut: 'Round Brilliant', carat: '1.50', color: 'F', clarity: 'VS1', value: '18500' }
            ],
            settingDescription: 'Classic 6-prong solitaire setting with knife-edge band',
            condition: 'Excellent - Like new condition',
            totalValue: '22800',
            photos: ['https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=300'],
            date: '2025-10-28',
            appraisalNumber: 'APR-2025-001',
            status: 'completed',
            template: 'classic'
        },
        {
            id: 'APR-2025-002',
            customerName: 'Michael Chen',
            customerEmail: 'mchen@email.com',
            customerPhone: '(555) 234-5678',
            jewelryType: 'Tennis Bracelet',
            metalType: 'White Gold',
            metalPurity: '18K',
            metalWeight: '12.8',
            gemstones: [
                { id: '1', species: 'Diamond', cut: 'Round Brilliant', carat: '5.00', color: 'G-H', clarity: 'VS2-SI1', value: '24000' }
            ],
            settingDescription: '40 round brilliant diamonds in 4-prong settings with concealed clasp',
            condition: 'Excellent',
            totalValue: '28500',
            photos: ['https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=300'],
            date: '2025-10-25',
            appraisalNumber: 'APR-2025-002',
            status: 'sent',
            template: 'modern'
        },
        {
            id: 'APR-2025-003',
            customerName: 'Emily Rodriguez',
            customerEmail: 'emily.r@email.com',
            customerPhone: '(555) 345-6789',
            jewelryType: 'Pendant Necklace',
            metalType: 'Yellow Gold',
            metalPurity: '14K',
            metalWeight: '3.5',
            gemstones: [
                { id: '1', species: 'Sapphire', cut: 'Oval', carat: '2.20', color: 'Royal Blue', clarity: 'Eye Clean', value: '8500' },
                { id: '2', species: 'Diamond', cut: 'Round Brilliant', carat: '0.35', color: 'G', clarity: 'VS2', value: '850' }
            ],
            settingDescription: 'Oval sapphire halo setting with 12 round brilliant diamond accents',
            condition: 'Very Good',
            totalValue: '11200',
            photos: ['https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=300'],
            date: '2025-10-22',
            appraisalNumber: 'APR-2025-003',
            status: 'completed',
            template: 'detailed'
        },
        {
            id: 'APR-2025-004',
            customerName: 'David Thompson',
            customerEmail: 'dthompson@email.com',
            customerPhone: '(555) 456-7890',
            jewelryType: 'Wedding Band Set',
            metalType: 'White Gold',
            metalPurity: '14K',
            metalWeight: '8.6',
            gemstones: [
                { id: '1', species: 'Diamond', cut: 'Round Brilliant', carat: '0.75', color: 'H', clarity: 'SI1', value: '2200' }
            ],
            settingDescription: 'Matching his and hers wedding bands with channel-set diamonds',
            condition: 'Excellent',
            totalValue: '4500',
            photos: ['https://images.unsplash.com/photo-1617038260897-41a1f14a8ca0?w=300'],
            date: '2025-10-20',
            appraisalNumber: 'APR-2025-004',
            status: 'completed',
            template: 'classic'
        },
        {
            id: 'APR-2025-005',
            customerName: 'Jessica Martinez',
            customerEmail: 'jmartinez@email.com',
            customerPhone: '(555) 567-8901',
            jewelryType: 'Eternity Band',
            metalType: 'Platinum',
            metalPurity: '950 Platinum',
            metalWeight: '6.2',
            gemstones: [
                { id: '1', species: 'Diamond', cut: 'Princess', carat: '3.50', color: 'F-G', clarity: 'VS1-VS2', value: '16500' }
            ],
            settingDescription: '25 princess-cut diamonds in shared prong setting',
            condition: 'Excellent',
            totalValue: '21000',
            photos: ['https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=300'],
            date: '2025-10-18',
            appraisalNumber: 'APR-2025-005',
            status: 'draft',
            template: 'modern'
        },
        {
            id: 'APR-2025-006',
            customerName: 'Robert Anderson',
            customerEmail: 'randerson@email.com',
            customerPhone: '(555) 678-9012',
            jewelryType: 'Cocktail Ring',
            metalType: 'Yellow Gold',
            metalPurity: '18K',
            metalWeight: '7.8',
            gemstones: [
                { id: '1', species: 'Emerald', cut: 'Emerald Cut', carat: '3.80', color: 'Vivid Green', clarity: 'Minor Inclusions', value: '15200' },
                { id: '2', species: 'Diamond', cut: 'Baguette', carat: '1.20', color: 'F', clarity: 'VVS2', value: '6800' }
            ],
            settingDescription: 'Colombian emerald center stone with baguette diamond side stones',
            condition: 'Excellent',
            totalValue: '26500',
            photos: ['https://images.unsplash.com/photo-1603561596112-0a132b757442?w=300'],
            date: '2025-10-15',
            appraisalNumber: 'APR-2025-006',
            status: 'completed',
            template: 'detailed'
        },
        {
            id: 'APR-2025-007',
            customerName: 'Lisa Williams',
            customerEmail: 'lwilliams@email.com',
            customerPhone: '(555) 789-0123',
            jewelryType: 'Stud Earrings',
            metalType: 'White Gold',
            metalPurity: '14K',
            metalWeight: '1.8',
            gemstones: [
                { id: '1', species: 'Diamond', cut: 'Round Brilliant', carat: '2.00', color: 'G', clarity: 'SI1', value: '9200' }
            ],
            settingDescription: '4-prong martini settings (1.00ct each)',
            condition: 'Excellent',
            totalValue: '11500',
            photos: ['https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?w=300'],
            date: '2025-10-12',
            appraisalNumber: 'APR-2025-007',
            status: 'sent',
            template: 'classic'
        },
        {
            id: 'APR-2025-008',
            customerName: 'Christopher Lee',
            customerEmail: 'clee@email.com',
            customerPhone: '(555) 890-1234',
            jewelryType: 'Anniversary Ring',
            metalType: 'Rose Gold',
            metalPurity: '14K',
            metalWeight: '5.1',
            gemstones: [
                { id: '1', species: 'Ruby', cut: 'Oval', carat: '1.80', color: 'Pigeon Blood Red', clarity: 'Eye Clean', value: '12000' },
                { id: '2', species: 'Diamond', cut: 'Round Brilliant', carat: '0.50', color: 'F', clarity: 'VS1', value: '1800' }
            ],
            settingDescription: 'Burma ruby center with diamond halo in vintage-inspired setting',
            condition: 'Excellent',
            totalValue: '16200',
            photos: ['https://images.unsplash.com/photo-1611591437281-460bfbe1220a?w=300'],
            date: '2025-10-10',
            appraisalNumber: 'APR-2025-008',
            status: 'completed',
            template: 'detailed'
        },
        {
            id: 'APR-2025-009',
            customerName: 'Amanda Taylor',
            customerEmail: 'ataylor@email.com',
            customerPhone: '(555) 901-2345',
            jewelryType: 'Pearl Necklace',
            metalType: 'White Gold',
            metalPurity: '18K',
            metalWeight: '4.2',
            gemstones: [
                { id: '1', species: 'Pearl', cut: 'Round', carat: '45pcs', color: 'White', clarity: 'AAA', value: '8900' }
            ],
            settingDescription: 'Graduated Akoya pearl strand (6-9mm) with diamond clasp',
            condition: 'Excellent',
            totalValue: '12400',
            photos: ['https://images.unsplash.com/photo-1506630448388-4e683c67ddb0?w=300'],
            date: '2025-10-08',
            appraisalNumber: 'APR-2025-009',
            status: 'completed',
            template: 'classic'
        },
        {
            id: 'APR-2025-010',
            customerName: 'Daniel Kim',
            customerEmail: 'dkim@email.com',
            customerPhone: '(555) 012-3456',
            jewelryType: 'Cufflinks',
            metalType: 'Platinum',
            metalPurity: '950 Platinum',
            metalWeight: '12.5',
            gemstones: [
                { id: '1', species: 'Onyx', cut: 'Cabochon', carat: '8.00', color: 'Black', clarity: 'Opaque', value: '1200' }
            ],
            settingDescription: 'Rectangular onyx cabochons with platinum bezels',
            condition: 'Very Good',
            totalValue: '3800',
            photos: ['https://images.unsplash.com/photo-1610758815058-48241c4e139f?w=300'],
            date: '2025-10-05',
            appraisalNumber: 'APR-2025-010',
            status: 'archived',
            template: 'modern'
        },
        {
            id: 'APR-2025-011',
            customerName: 'Maria Garcia',
            customerEmail: 'mgarcia@email.com',
            customerPhone: '(555) 123-9876',
            jewelryType: 'Three Stone Ring',
            metalType: 'Platinum',
            metalPurity: '950 Platinum',
            metalWeight: '5.8',
            gemstones: [
                { id: '1', species: 'Diamond', cut: 'Emerald Cut', carat: '2.50', color: 'D', clarity: 'VVS1', value: '35000' }
            ],
            settingDescription: '1.50ct center with two 0.50ct side stones in classic three-stone design',
            condition: 'Excellent',
            totalValue: '42500',
            photos: ['https://images.unsplash.com/photo-1603561596112-0a132b757442?w=300'],
            date: '2025-10-03',
            appraisalNumber: 'APR-2025-011',
            status: 'completed',
            template: 'detailed'
        },
        {
            id: 'APR-2025-012',
            customerName: 'James Wilson',
            customerEmail: 'jwilson@email.com',
            customerPhone: '(555) 234-8765',
            jewelryType: 'Bangle Bracelet',
            metalType: 'Yellow Gold',
            metalPurity: '22K',
            metalWeight: '28.5',
            gemstones: [],
            settingDescription: 'Solid 22K yellow gold bangle with intricate filigree work',
            condition: 'Very Good',
            totalValue: '5200',
            photos: ['https://images.unsplash.com/photo-1611591437281-460bfbe1220a?w=300'],
            date: '2025-09-30',
            appraisalNumber: 'APR-2025-012',
            status: 'completed',
            template: 'classic'
        },
        {
            id: 'APR-2025-013',
            customerName: 'Sophia Brown',
            customerEmail: 'sbrown@email.com',
            customerPhone: '(555) 345-7654',
            jewelryType: 'Hoop Earrings',
            metalType: 'White Gold',
            metalPurity: '18K',
            metalWeight: '6.4',
            gemstones: [
                { id: '1', species: 'Diamond', cut: 'Round Brilliant', carat: '3.00', color: 'H', clarity: 'SI1', value: '12500' }
            ],
            settingDescription: 'Inside-out hoop design with 60 round brilliant diamonds',
            condition: 'Excellent',
            totalValue: '16800',
            photos: ['https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?w=300'],
            date: '2025-09-28',
            appraisalNumber: 'APR-2025-013',
            status: 'completed',
            template: 'modern'
        },
        {
            id: 'APR-2025-014',
            customerName: 'Thomas White',
            customerEmail: 'twhite@email.com',
            customerPhone: '(555) 456-6543',
            jewelryType: 'Signet Ring',
            metalType: 'Yellow Gold',
            metalPurity: '18K',
            metalWeight: '15.2',
            gemstones: [
                { id: '1', species: 'Bloodstone', cut: 'Cabochon', carat: '5.50', color: 'Green with Red', clarity: 'Opaque', value: '650' }
            ],
            settingDescription: 'Traditional signet ring with oval bloodstone cabochon',
            condition: 'Good',
            totalValue: '3800',
            photos: ['https://images.unsplash.com/photo-1611591437281-460bfbe1220a?w=300'],
            date: '2025-09-25',
            appraisalNumber: 'APR-2025-014',
            status: 'draft',
            template: 'classic'
        },
        {
            id: 'APR-2025-015',
            customerName: 'Jennifer Davis',
            customerEmail: 'jdavis@email.com',
            customerPhone: '(555) 567-5432',
            jewelryType: 'Charm Bracelet',
            metalType: 'Silver',
            metalPurity: '925 Sterling',
            metalWeight: '18.5',
            gemstones: [],
            settingDescription: 'Sterling silver link bracelet with 8 assorted charms',
            condition: 'Very Good',
            totalValue: '850',
            photos: ['https://images.unsplash.com/photo-1611591437281-460bfbe1220a?w=300'],
            date: '2025-09-22',
            appraisalNumber: 'APR-2025-015',
            status: 'archived',
            template: 'modern'
        }
    ]);
    const handleInputChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };
    // Custom field handlers
    const handleAddCustomField = (step) => {
        const newField = {
            id: `field_${Date.now()}`,
            fieldName: '',
            fieldValue: ''
        };
        if (step === 1) {
            setCustomFieldsStep1([...customFieldsStep1, newField]);
        }
        else if (step === 2) {
            setCustomFieldsStep2([...customFieldsStep2, newField]);
        }
        else if (step === 5) {
            setCustomFieldsStep5([...customFieldsStep5, newField]);
        }
    };
    const handleCustomFieldChange = (step, id, field, value) => {
        if (step === 1) {
            setCustomFieldsStep1(customFieldsStep1.map(f => f.id === id ? { ...f, [field]: value } : f));
        }
        else if (step === 2) {
            setCustomFieldsStep2(customFieldsStep2.map(f => f.id === id ? { ...f, [field]: value } : f));
        }
        else if (step === 5) {
            setCustomFieldsStep5(customFieldsStep5.map(f => f.id === id ? { ...f, [field]: value } : f));
        }
    };
    const handleRemoveCustomField = (step, id) => {
        if (step === 1) {
            setCustomFieldsStep1(customFieldsStep1.filter(f => f.id !== id));
        }
        else if (step === 2) {
            setCustomFieldsStep2(customFieldsStep2.filter(f => f.id !== id));
        }
        else if (step === 5) {
            setCustomFieldsStep5(customFieldsStep5.filter(f => f.id !== id));
        }
    };
    const handleAddGemstone = () => {
        const newGemstone = {
            id: Date.now().toString(),
            species: '',
            cut: '',
            carat: '',
            color: '',
            clarity: '',
            value: ''
        };
        setGemstones([...gemstones, newGemstone]);
        // Automatically put the new gemstone in edit mode
        setEditingGemId(newGemstone.id);
    };
    const handleRemoveGemstone = (id) => {
        setGemstones(gemstones.filter(g => g.id !== id));
        if (editingGemId === id) {
            setEditingGemId(null);
        }
    };
    const handleEditGemstone = (id) => {
        setEditingGemId(editingGemId === id ? null : id);
    };
    const handleGemstoneChange = (id, field, value) => {
        setGemstones(gemstones.map(g => g.id === id ? { ...g, [field]: value } : g));
    };
    const handlePhotoUpload = () => {
        if (mode === 'test') {
            // Real photo upload in test mode
            fileInputRef.current?.click();
        }
        else {
            // Mock photo upload in demo mode
            const jewelryImages = [
                'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=400',
                'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=400',
                'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=400',
                'https://images.unsplash.com/photo-1611591437281-460bfbe1220a?w=400',
                'https://images.unsplash.com/photo-1603561596112-0a132b757442?w=400'
            ];
            const randomImage = jewelryImages[Math.floor(Math.random() * jewelryImages.length)];
            setPhotos([...photos, `${randomImage}&sig=${Date.now()}`]);
        }
    };
    const handleFileSelect = async (event) => {
        const file = event.target.files?.[0];
        if (!file)
            return;
        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Please select an image file');
            return;
        }
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert('File size must be less than 5MB');
            return;
        }
        try {
            setUploadingPhoto(true);
            // Create form data
            const formData = new FormData();
            formData.append('file', file);
            // Use fetch directly for file upload (apiClient would stringify it)
            const apiUrl = 'https://func-app-royalgem-b1.azurewebsites.net/api/appraisals-upload-photo';
            const uploadResponse = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                    // Don't set Content-Type - let browser set it with boundary
                },
                body: formData
            });
            const result = await uploadResponse.json();
            if (uploadResponse.ok && result.success) {
                // Add blob URL to photos array
                setPhotos([...photos, result.url]);
                console.log('✅ Photo uploaded:', result.url);
            }
            else {
                console.error('Upload failed:', result);
                alert(`❌ Failed to upload photo: ${result.error || 'Unknown error'}`);
            }
        }
        catch (error) {
            console.error('Error uploading photo:', error);
            alert('❌ Error uploading photo');
        }
        finally {
            setUploadingPhoto(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };
    const handleRemovePhoto = (index) => {
        setPhotos(photos.filter((_, i) => i !== index));
    };
    const handleNextStep = () => {
        // Validate current step
        const errors = validateStep(currentStep);
        if (errors.length > 0) {
            setValidationErrors(errors);
            return;
        }
        setValidationErrors([]);
        if (currentStep < 5) {
            setCurrentStep(currentStep + 1);
        }
    };
    const handlePrevStep = () => {
        setValidationErrors([]);
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };
    const validateStep = (step) => {
        const errors = [];
        if (step === 1) {
            if (!formData.customerName.trim())
                errors.push('Customer name is required');
        }
        if (step === 2) {
            if (!formData.jewelryType)
                errors.push('Jewelry type is required');
            if (!formData.metalType)
                errors.push('Metal description is required');
            // metalPurity is optional - can be included in metalType description or custom fields
        }
        if (step === 5) {
            if (!formData.totalValue)
                errors.push('Total appraised value is required');
        }
        return errors;
    };
    const handleGenerateAppraisal = () => {
        const errors = validateStep(5);
        if (errors.length > 0) {
            setValidationErrors(errors);
            return;
        }
        setShowPDFPreview(true);
    };
    const handleViewAppraisal = (appraisal) => {
        setSelectedAppraisal(appraisal);
        setShowPDFPreview(true);
    };
    const handleSaveDraft = () => {
        alert('Draft Saved!\n\nThis appraisal has been saved as a draft. You can resume editing it later from the History tab.');
    };
    const handleDuplicateAppraisal = (appraisal) => {
        alert(`Duplicate Appraisal\n\nCreating a new appraisal based on:\n${appraisal.customerName} - ${getAppraisalField(appraisal, 'jewelryType')}\n\nThe form will be pre-filled with this appraisal's details.`);
        setActiveTab('create');
    };
    const handleDeleteAppraisal = async (appraisal) => {
        const confirmed = window.confirm(`Are you sure you want to delete this appraisal?\n\n` +
            `Customer: ${appraisal.customerName}\n` +
            `Item: ${getAppraisalField(appraisal, 'jewelryType')}\n` +
            `Appraisal #: ${appraisal.appraisalNumber}\n\n` +
            `This action cannot be undone.`);
        if (!confirmed)
            return;
        try {
            const response = await apiClient.post('appraisals-delete', {
                id: appraisal.id
            });
            if (response.ok && response.data?.success) {
                alert('Appraisal deleted successfully');
                // Refresh the appraisals list
                loadAppraisals();
            }
            else {
                alert(`Failed to delete appraisal: ${response.data?.error || 'Unknown error'}`);
            }
        }
        catch (error) {
            console.error('Error deleting appraisal:', error);
            alert('Failed to delete appraisal. Please try again.');
        }
    };
    const handleEditAppraisal = (appraisal) => {
        console.log('📝 Editing appraisal:', appraisal);
        // Set editing mode
        setEditingAppraisalId(appraisal.id);
        setEditingAppraisalNumber(appraisal.appraisalNumber);
        // Load appraisal data into form
        // Check if data is in nested formData object (real API appraisals) or top-level (mock)
        const sourceData = appraisal.formData || appraisal;
        setFormData({
            customerName: appraisal.customerName || sourceData.customerName || '',
            customerEmail: appraisal.customerEmail || sourceData.customerEmail || '',
            customerPhone: sourceData.customerPhone || '',
            customerAddress: appraisal.customerAddress || sourceData.customerAddress || '',
            jewelryType: sourceData.jewelryType || '',
            metalType: sourceData.metalType || '',
            metalPurity: sourceData.metalPurity || '',
            metalWeight: sourceData.metalWeight || '',
            settingDescription: sourceData.settingDescription || '',
            condition: sourceData.condition || 'excellent',
            totalValue: sourceData.totalValue || '',
            template: (sourceData.template || appraisal.template || 'classic'),
            itemDescription: sourceData.itemDescription || '',
            diamondRingValue: sourceData.diamondRingValue || '',
            goldRingValue: sourceData.goldRingValue || '',
            goldPriceReference: sourceData.goldPriceReference || '',
            appraiser1Name: sourceData.appraiser1Name || 'Aneesh K. Jain',
            appraiser1Title: sourceData.appraiser1Title || 'GIA, Graduate Diamonds',
            appraiser2Name: sourceData.appraiser2Name || 'Michael Gold',
            appraiser2Title: sourceData.appraiser2Title || 'GIA, Graduate Gemologist',
            companyName: sourceData.companyName || 'Aneesh Jain, LLC.',
            companyAddress: sourceData.companyAddress || '525 Vine St, 23rd floor\nCincinnati, OH 45202',
            companyPhone: sourceData.companyPhone || '513-349-8816',
            showRoyalGemsBranding: sourceData.showRoyalGemsBranding || false
        });
        // Load gemstones
        setGemstones(appraisal.gemstones || []);
        // Load photos
        setPhotos(appraisal.photos || []);
        // Load custom fields
        setCustomFieldsStep1(appraisal.customFieldsStep1 || []);
        setCustomFieldsStep2(appraisal.customFieldsStep2 || []);
        setCustomFieldsStep5(appraisal.customFieldsStep5 || []);
        // Switch to create tab and reset to first step
        setActiveTab('create');
        setCurrentStep(1);
        console.log('✅ Appraisal loaded for editing');
    };
    const handleCreateNew = () => {
        // Clear editing mode
        setEditingAppraisalId(null);
        setEditingAppraisalNumber(null);
        // Reset form to defaults
        setFormData({
            customerName: '',
            customerEmail: '',
            customerPhone: '',
            customerAddress: '',
            jewelryType: '',
            metalType: '',
            metalPurity: '',
            metalWeight: '',
            settingDescription: '',
            condition: 'excellent',
            totalValue: '',
            template: 'classic',
            itemDescription: '',
            diamondRingValue: '',
            goldRingValue: '',
            goldPriceReference: '',
            appraiser1Name: 'Aneesh K. Jain',
            appraiser1Title: 'GIA, Graduate Diamonds',
            appraiser2Name: 'Michael Gold',
            appraiser2Title: 'GIA, Graduate Gemologist',
            companyName: 'Aneesh Jain, LLC.',
            companyAddress: '525 Vine St, 23rd floor\nCincinnati, OH 45202',
            companyPhone: '513-349-8816',
            showRoyalGemsBranding: false
        });
        // Clear all other data
        setGemstones([]);
        setPhotos([]);
        setCustomFieldsStep1([]);
        setCustomFieldsStep2([]);
        setCustomFieldsStep5([]);
        setSignatureDrawn(false);
        setValidationErrors([]);
        // Switch to create tab and reset to first step
        setActiveTab('create');
        setCurrentStep(1);
        console.log('✅ New appraisal form initialized');
    };
    const handleExportCSV = () => {
        try {
            const sourceAppraisals = mode === 'test' ? realAppraisals : mockAppraisals;
            if (sourceAppraisals.length === 0) {
                notify.warning('No Data', 'No appraisals to export');
                return;
            }
            const headers = ['Appraisal Number', 'Date', 'Customer Name', 'Jewelry Type', 'Total Value', 'Status'];
            const rows = sourceAppraisals.map(a => [
                a.appraisalNumber || '',
                a.createdAt ? new Date(a.createdAt).toLocaleDateString() : (a.date || ''),
                a.customerName || '',
                getAppraisalField(a, 'jewelryType') || '',
                `$${(parseFloat(getAppraisalField(a, 'totalValue') || '0') || 0).toLocaleString()}`,
                a.status || ''
            ]);
            const csvContent = [headers, ...rows]
                .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
                .join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `appraisals-${new Date().toISOString().split('T')[0]}.csv`;
            link.click();
            window.URL.revokeObjectURL(url);
            notify.success('Export Complete', `Exported ${sourceAppraisals.length} appraisals to CSV`);
        }
        catch (error) {
            console.error('CSV export error:', error);
            notify.error('Export Failed', 'Failed to export appraisals');
        }
    };
    const handleSendEmail = () => {
        setShowEmailPreview(true);
    };
    const handleDownloadPDF = async () => {
        try {
            setDownloadingPDF(true);
            const appraisalNumber = selectedAppraisal?.appraisalNumber || getPreviewAppraisalNumber();
            const customerName = (selectedAppraisal?.customerName || formData.customerName || 'Customer').replace(/\s+/g, '_');
            const filename = `${appraisalNumber}_${customerName}_Appraisal.pdf`;
            // Convert all images to base64 to avoid CSP issues
            const photoUrls = selectedAppraisal?.photos || photos;
            console.log('📸 Converting photos to base64:', photoUrls);
            const photosBase64 = await Promise.all(photoUrls.map(async (url, index) => {
                try {
                    console.log(`🔄 Converting photo ${index + 1}/${photoUrls.length}:`, url);
                    const base64 = await urlToBase64(url);
                    console.log(`✅ Photo ${index + 1} converted successfully (length: ${base64.length})`);
                    return base64;
                }
                catch (error) {
                    console.error(`❌ Failed to convert photo ${index + 1} to base64:`, url, error);
                    return url; // Fallback to original URL if conversion fails
                }
            }));
            // Convert hardcoded signature and logo images to base64
            const DUAL_SIGNATURE_IMAGE_URL = 'https://blobaneesh.blob.core.windows.net/images/appraisals/sig.bmp';
            const DIAMOND_LOGO_URL = 'https://blobaneesh.blob.core.windows.net/images/appraisals/diamondvector.png';
            let signatureBase64 = DUAL_SIGNATURE_IMAGE_URL;
            let logoBase64 = DIAMOND_LOGO_URL;
            console.log('🔏 Converting signature image:', DUAL_SIGNATURE_IMAGE_URL);
            try {
                signatureBase64 = await urlToBase64(DUAL_SIGNATURE_IMAGE_URL);
                console.log('✅ Signature converted successfully (length:', signatureBase64.length + ')');
            }
            catch (error) {
                console.error('❌ Failed to convert signature to base64:', error);
                console.error('Error details:', error instanceof Error ? error.message : error);
            }
            console.log('💎 Converting logo image:', DIAMOND_LOGO_URL);
            try {
                logoBase64 = await urlToBase64(DIAMOND_LOGO_URL);
                console.log('✅ Logo converted successfully (length:', logoBase64.length + ')');
            }
            catch (error) {
                console.error('❌ Failed to convert logo to base64:', error);
                console.error('Error details:', error instanceof Error ? error.message : error);
            }
            console.log('📊 Summary - Images to include in PDF:');
            console.log('  - Photos:', photosBase64.length, 'items');
            console.log('  - Logo base64?', logoBase64.startsWith('data:'));
            console.log('  - Signature base64?', signatureBase64.startsWith('data:'));
            // Use selected appraisal's data if viewing from history, otherwise use current form
            const pdfFormData = selectedAppraisal?.formData || formData;
            console.log('📄 PDF Data Source:', selectedAppraisal ? 'Selected Appraisal' : 'Current Form');
            console.log('📋 Form Data being used:', pdfFormData);
            // Generate PDF using @react-pdf/renderer
            const blob = await pdf(_jsx(AppraisalPDFDocument, { mode: mode, formData: pdfFormData, gemstones: selectedAppraisal?.gemstones || gemstones, photos: photosBase64, customFieldsStep1: selectedAppraisal?.customFieldsStep1 || customFieldsStep1, customFieldsStep2: selectedAppraisal?.customFieldsStep2 || customFieldsStep2, customFieldsStep5: selectedAppraisal?.customFieldsStep5 || customFieldsStep5, selectedAppraisal: selectedAppraisal, signatureImageBase64: signatureBase64, logoImageBase64: logoBase64 })).toBlob();
            // Download the PDF
            saveAs(blob, filename);
            console.log('✅ PDF downloaded:', filename);
        }
        catch (error) {
            console.error('Error generating PDF:', error);
            alert('❌ Error generating PDF. Please try again.');
        }
        finally {
            setDownloadingPDF(false);
        }
    };
    // Send appraisal via Gmail: downloads the PDF locally, then opens a Gmail
    // compose tab with recipient/subject/body pre-filled. Web browsers do not
    // allow programmatic file attachments to webmail, so the user drags the
    // just-downloaded PDF into the compose window.
    const handleSendViaGmail = async () => {
        const recipientEmail = selectedAppraisal?.customerEmail || formData.customerEmail;
        if (!recipientEmail) {
            notify.warning('Missing Email', 'Please enter a customer email address first.');
            return;
        }
        try {
            // 1. Generate and download the PDF so it lands in the user's downloads
            await handleDownloadPDF();
            // 2. Build the email content
            const appraisalNumber = selectedAppraisal?.appraisalNumber || getPreviewAppraisalNumber();
            const customerName = selectedAppraisal?.customerName || formData.customerName || 'Valued Customer';
            const jewelryType = formData.jewelryType || 'jewelry item';
            const totalValue = parseInt(formData.totalValue || '0').toLocaleString();
            const validUntil = new Date(new Date().setFullYear(new Date().getFullYear() + 2)).toLocaleDateString();
            const companyName = mode === 'test' && !formData.showRoyalGemsBranding
                ? (formData.companyName || 'Royal Gems, Inc.')
                : 'Royal Gems, Inc.';
            const appraiserName = mode === 'test' ? (formData.appraiser1Name || 'Our Gemologist') : 'John Smith';
            const appraiserTitle = mode === 'test' ? (formData.appraiser1Title || 'Graduate Gemologist') : 'Graduate Gemologist (GG)';
            const companyPhone = mode === 'test' ? (formData.companyPhone || '(555) 123-4567') : '(555) 123-4567';
            const subject = `Your Jewelry Appraisal ${appraisalNumber} - ${companyName}`;
            const body = `Dear ${customerName},

Thank you for choosing ${companyName} for your jewelry appraisal needs. Please find attached your official jewelry appraisal document for insurance purposes.

Appraisal Summary:
  Item: ${jewelryType}
  Appraisal Number: ${appraisalNumber}
  Appraised Value: $${totalValue} USD
  Valid Until: ${validUntil}

Please provide this appraisal to your insurance provider for coverage purposes. We recommend updating your appraisal every 2-3 years to reflect current market conditions.

If you have any questions or need additional documentation, please don't hesitate to contact us.

Best regards,
${appraiserName}
${appraiserTitle}
${companyName}
${companyPhone}

---
This email and any attachments are confidential and intended solely for the recipient.`;
            // 3. Open Gmail compose in a new tab with everything pre-filled (BCC for record-keeping)
            const bccEmail = 'aneesh@royalgemsusa.com';
            const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(recipientEmail)}&bcc=${encodeURIComponent(bccEmail)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
            window.open(gmailUrl, '_blank', 'noopener,noreferrer');
            // 4. Let the user know to attach the downloaded PDF
            notify.success('Gmail Opened', 'The PDF was downloaded and Gmail is open in a new tab. Drag the downloaded PDF into the Gmail window to attach it, then click Send.');
            setShowEmailPreview(false);
        }
        catch (error) {
            console.error('Error preparing email:', error);
            notify.error('Email Failed', error instanceof Error ? error.message : 'Failed to prepare email');
        }
    };
    const wizardSteps = [
        { number: 1, label: 'Customer Info' },
        { number: 2, label: 'Jewelry Details' },
        { number: 3, label: 'Gemstones' },
        { number: 4, label: 'Photos' },
        { number: 5, label: 'Review & Generate' }
    ];
    // Calculate progress percentage
    const progressPercentage = Math.round((currentStep / wizardSteps.length) * 100);
    // Auto-calculate total value
    const autoCalculatedValue = useMemo(() => {
        const gemstoneTotal = gemstones.reduce((sum, gem) => {
            const value = parseFloat(gem.value.replace(/,/g, '')) || 0;
            return sum + value;
        }, 0);
        // Mock metal value calculation (simplified)
        const metalWeight = parseFloat(formData.metalWeight) || 0;
        const metalValuePerGram = formData.metalType === 'Platinum' ? 30 :
            formData.metalType === 'Yellow Gold' ? 60 :
                formData.metalType === 'White Gold' ? 55 :
                    formData.metalType === 'Rose Gold' ? 55 : 10;
        const metalValue = metalWeight * metalValuePerGram;
        return Math.round(gemstoneTotal + metalValue + 500); // +500 for labor/setting
    }, [gemstones, formData.metalWeight, formData.metalType]);
    // Use auto-calculated value if user hasn't manually entered one
    useEffect(() => {
        if (!formData.totalValue && autoCalculatedValue > 0) {
            setFormData(prev => ({ ...prev, totalValue: autoCalculatedValue.toString() }));
        }
    }, [autoCalculatedValue, formData.totalValue]);
    // Filter and sort appraisals
    const filteredAppraisals = useMemo(() => {
        // Use real appraisals in test mode, mock appraisals in mock mode
        const sourceAppraisals = mode === 'test' ? realAppraisals : mockAppraisals;
        let filtered = [...sourceAppraisals];
        // Search filter with safe access
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(a => (a.customerName || '').toLowerCase().includes(query) ||
                (a.appraisalNumber || '').toLowerCase().includes(query) ||
                (a.formData?.jewelryType || a.jewelryType || '').toLowerCase().includes(query));
        }
        // Status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(a => a.status === statusFilter);
        }
        // Date filter with safe access (real appraisals use createdAt, mock use date)
        if (dateFilter !== 'all') {
            const now = new Date();
            const cutoffDate = new Date();
            if (dateFilter === '7days')
                cutoffDate.setDate(now.getDate() - 7);
            if (dateFilter === '30days')
                cutoffDate.setDate(now.getDate() - 30);
            if (dateFilter === '90days')
                cutoffDate.setDate(now.getDate() - 90);
            filtered = filtered.filter(a => {
                const itemDate = a.createdAt || a.date;
                return itemDate && new Date(itemDate) >= cutoffDate;
            });
        }
        // Sort with safe access
        filtered.sort((a, b) => {
            if (sortBy === 'date') {
                const dateA = new Date(a.createdAt || a.date || 0).getTime();
                const dateB = new Date(b.createdAt || b.date || 0).getTime();
                return dateB - dateA;
            }
            else if (sortBy === 'value') {
                const valueA = parseInt((a.formData?.totalValue || a.totalValue || '0'));
                const valueB = parseInt((b.formData?.totalValue || b.totalValue || '0'));
                return valueB - valueA;
            }
            else if (sortBy === 'customer') {
                return (a.customerName || '').localeCompare(b.customerName || '');
            }
            return 0;
        });
        return filtered;
    }, [mode, mockAppraisals, realAppraisals, searchQuery, statusFilter, dateFilter, sortBy]);
    // Calculate statistics with safe access
    const statistics = useMemo(() => {
        const sourceAppraisals = mode === 'test' ? realAppraisals : mockAppraisals;
        const total = sourceAppraisals.length;
        const totalValue = sourceAppraisals.reduce((sum, a) => {
            const value = a.formData?.totalValue || a.totalValue || '0';
            return sum + parseInt(value);
        }, 0);
        const avgValue = total > 0 ? Math.round(totalValue / total) : 0;
        const last30Days = sourceAppraisals.filter(a => {
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            const itemDate = a.createdAt || a.date;
            return itemDate && new Date(itemDate) >= thirtyDaysAgo;
        }).length;
        const completed = sourceAppraisals.filter(a => a.status === 'completed').length;
        return { total, totalValue, avgValue, last30Days, completed };
    }, [mode, mockAppraisals, realAppraisals]);
    // Generate preview appraisal number with today's date (APR-MMDDYYYY-NEW)
    const getPreviewAppraisalNumber = () => {
        const now = new Date();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const year = now.getFullYear();
        return `APR-${month}${day}${year}-NEW`;
    };
    // Helper to safely get appraisal field (handles both mock and real appraisal structures)
    const getAppraisalField = (appraisal, field) => {
        // Real appraisals from API have formData nested, mock appraisals have flat structure
        if (field === 'date') {
            return appraisal.createdAt || appraisal.date;
        }
        if (field === 'jewelryType' || field === 'metalType' || field === 'metalPurity' || field === 'totalValue' || field === 'template') {
            return appraisal.formData?.[field] || appraisal[field] || '';
        }
        return appraisal[field] || '';
    };
    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return '#10b981';
            case 'sent': return '#3b82f6';
            case 'draft': return '#f59e0b';
            case 'archived': return '#6b7280';
            default: return 'var(--border-color)';
        }
    };
    const getStatusLabel = (status) => {
        switch (status) {
            case 'completed': return 'COMPLETED';
            case 'sent': return 'SENT TO CUSTOMER';
            case 'draft': return 'DRAFT';
            case 'archived': return 'ARCHIVED';
            default: return status.toUpperCase();
        }
    };
    return (_jsxs(PageContainer, { children: [_jsx(HeaderSection, { children: _jsxs("div", { children: [_jsx(PageTitle, { children: "RG Appraisal" }), _jsx(Text, { style: { marginTop: '8px' }, children: "Create appraisals for finished jewelry pieces" })] }) }), _jsxs(StatsOverview, { id: "tour-stats-dashboard", children: [_jsxs(StatCard, { "$color": "#10b981", children: [_jsx("div", { className: "stat-icon", children: _jsx(FaFileAlt, {}) }), _jsx("div", { className: "stat-value", children: statistics.total }), _jsx("div", { className: "stat-label", children: "Total Appraisals" })] }), _jsxs(StatCard, { "$color": "#3b82f6", children: [_jsx("div", { className: "stat-icon", children: _jsx(FaDollarSign, {}) }), _jsxs("div", { className: "stat-value", children: ["$", (statistics.totalValue / 1000).toFixed(0), "K"] }), _jsx("div", { className: "stat-label", children: "Total Value" })] }), _jsxs(StatCard, { "$color": "#f59e0b", children: [_jsx("div", { className: "stat-icon", children: _jsx(FaChartLine, {}) }), _jsxs("div", { className: "stat-value", children: ["$", (statistics.avgValue / 1000).toFixed(1), "K"] }), _jsx("div", { className: "stat-label", children: "Average Value" })] }), _jsxs(StatCard, { "$color": "#8b5cf6", children: [_jsx("div", { className: "stat-icon", children: _jsx(FaCalendarAlt, {}) }), _jsx("div", { className: "stat-value", children: statistics.last30Days }), _jsx("div", { className: "stat-label", children: "Last 30 Days" })] })] }), _jsxs(TabBar, { children: [_jsxs(Tab, { id: "tour-create-tab", "$active": activeTab === 'create', onClick: handleCreateNew, children: [_jsx(FaPlus, {}), "Create New Appraisal"] }), _jsxs(Tab, { id: "tour-history-tab", "$active": activeTab === 'history', onClick: () => setActiveTab('history'), children: [_jsx(FaFileAlt, {}), "Appraisal History (", mode === 'test' ? realAppraisals.length : mockAppraisals.length, ")"] })] }), activeTab === 'create' && (_jsxs(WizardContainer, { children: [editingAppraisalId && (_jsxs("div", { style: { marginBottom: '24px', padding: '16px', background: '#3b82f6', border: '2px solid #2563eb', display: 'flex', alignItems: 'center', gap: '12px' }, children: [_jsx(FaEdit, { style: { fontSize: '20px', color: '#fff' } }), _jsxs("div", { children: [_jsx(Text, { style: { fontSize: '14px', fontWeight: 700, marginBottom: '4px', color: '#fff' }, children: "Editing Mode" }), _jsx(Text, { style: { fontSize: '12px', marginBottom: 0, color: '#e0e7ff' }, children: "You are currently editing an existing appraisal. Changes will update the original record." })] })] })), mode === 'test' && (_jsxs("div", { style: { marginBottom: '32px', padding: '20px', background: 'var(--bg-tertiary)', border: '2px solid #10b981' }, children: [_jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }, children: [_jsx(Text, { style: { fontSize: '14px', fontWeight: 600, marginBottom: 0 }, children: "\uD83D\uDCCB Template Management" }), _jsxs(Button, { variant: "secondary", onClick: () => setShowSaveTemplateModal(true), style: { padding: '8px 16px', fontSize: '12px' }, children: [_jsx(FaSave, {}), " Save Current as Template"] })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Load from Template (Optional)" }), _jsxs(Select, { value: selectedTemplateId, onChange: (e) => {
                                            setSelectedTemplateId(e.target.value);
                                            if (e.target.value) {
                                                loadTemplate(e.target.value);
                                            }
                                        }, children: [_jsx("option", { value: "", children: "Start from scratch..." }), templates.map(t => (_jsxs("option", { value: t.id, children: [t.templateName, " - ", t.description || 'No description'] }, t.id)))] }), loadingTemplates && (_jsx(Text, { style: { fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }, children: "Loading templates..." })), templates.length === 0 && !loadingTemplates && (_jsx(Text, { style: { fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }, children: "No templates saved yet. Fill out the form and click \"Save Current as Template\" to create one." }))] })] })), _jsxs(ProgressIndicator, { children: [_jsxs("div", { className: "percentage", children: [progressPercentage, "% Complete"] }), _jsxs("div", { children: ["Step ", currentStep, " of ", wizardSteps.length] })] }), _jsxs(WizardSteps, { id: "tour-wizard-progress", children: [_jsx(ProgressBar, { "$progress": (currentStep - 1) / (wizardSteps.length - 1) * 80 + 10 }), wizardSteps.map(step => (_jsxs(WizardStep, { "$active": currentStep === step.number, "$completed": currentStep > step.number, children: [_jsx("div", { className: "step-circle", children: currentStep > step.number ? _jsx(FaCheckCircle, {}) : step.number }), _jsx("div", { className: "step-label", children: step.label })] }, step.number)))] }), validationErrors.length > 0 && (_jsxs(ValidationMessage, { children: [_jsx(FaBolt, {}), _jsx("div", { children: validationErrors.map((err, i) => (_jsx("div", { children: err }, i))) })] })), currentStep === 1 && (_jsxs("div", { children: [_jsx(SectionTitle, { style: { marginBottom: '24px' }, children: "Customer Information" }), _jsxs(FormGrid, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { children: "Customer Name *" }), _jsx(Input, { name: "customerName", value: formData.customerName, onChange: handleInputChange, placeholder: "Enter customer's full name", className: validationErrors.some(e => e.includes('name')) ? 'error' : '' })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Email Address" }), _jsx(Input, { type: "email", name: "customerEmail", value: formData.customerEmail, onChange: handleInputChange, placeholder: "customer@email.com" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Phone Number" }), _jsx(Input, { name: "customerPhone", value: formData.customerPhone, onChange: handleInputChange, placeholder: "(555) 123-4567" })] }), mode === 'test' && (_jsxs(FormGroup, { style: { gridColumn: '1 / -1' }, children: [_jsx(Label, { children: "Customer Address (Required for Insurance Appraisals)" }), _jsx(Textarea, { name: "customerAddress", value: formData.customerAddress, onChange: handleInputChange, placeholder: "1890 EAST 2ND ST.\nBROOKLYN, NY 11223", rows: 2 }), _jsx(Text, { style: { fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }, children: "Enter complete mailing address (street, city, state, zip)" })] }))] }), mode === 'test' && (_jsxs(CustomFieldsSection, { children: [_jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }, children: [_jsx(Text, { style: { fontSize: '13px', fontWeight: 600, marginBottom: 0 }, children: "Additional Customer Information (Custom Fields)" }), _jsxs(Button, { variant: "secondary", onClick: () => handleAddCustomField(1), style: { padding: '6px 12px', fontSize: '12px' }, children: [_jsx(FaPlus, {}), " Add Field"] })] }), customFieldsStep1.length === 0 ? (_jsx(Text, { style: { fontSize: '12px', color: 'var(--text-tertiary)', textAlign: 'center', marginBottom: 0 }, children: "Add custom fields for additional customer details" })) : (customFieldsStep1.map((field) => (_jsxs(CustomFieldItem, { children: [_jsx(CustomFieldInput, { type: "text", placeholder: "Field name (e.g., 'Policy Number')", value: field.fieldName, onChange: (e) => handleCustomFieldChange(1, field.id, 'fieldName', e.target.value) }), _jsx(CustomFieldInput, { type: "text", placeholder: "Value (e.g., 'POL-123456')", value: field.fieldValue, onChange: (e) => handleCustomFieldChange(1, field.id, 'fieldValue', e.target.value) }), _jsx(Button, { variant: "secondary", onClick: () => handleRemoveCustomField(1, field.id), style: { padding: '6px 12px', fontSize: '12px' }, children: _jsx(FaTrash, {}) })] }, field.id))))] }))] })), currentStep === 2 && (_jsxs("div", { children: [_jsx(SectionTitle, { style: { marginBottom: '24px' }, children: "Item Description & Jewelry Details" }), mode === 'test' && (_jsxs(_Fragment, { children: [_jsx("div", { style: { marginBottom: '16px', padding: '12px', background: 'rgba(16, 185, 129, 0.1)', border: '1px solid #10b981' }, children: _jsxs(Text, { style: { fontSize: '12px', color: 'var(--text-secondary)', marginBottom: 0 }, children: [_jsx("strong", { children: "Step 1 of 3:" }), " Write a complete narrative description of the jewelry piece"] }) }), _jsxs(FormGroup, { style: { marginBottom: '40px' }, children: [_jsxs(Label, { style: { fontSize: '15px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsx(FaFileAlt, {}), " Complete Item Description (Narrative) *"] }), _jsx("textarea", { name: "itemDescription", value: formData.itemDescription, onChange: handleInputChange, placeholder: "Example from SAM CHERA appraisal:\n\nA finely handcrafted ring featuring a 2.01 carat natural oval brilliant-cut diamond, elegantly mounted in a platinum (950) four-prong head with an 18-karat yellow gold high-polish shank. The design presents a harmonious contrast between the cool luster of platinum and the warm glow of gold\u2013a timeless aesthetic.", rows: 6, style: {
                                                    width: '100%',
                                                    padding: '16px',
                                                    background: 'var(--bg-secondary)',
                                                    border: '2px solid #10b981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: '14px',
                                                    lineHeight: '1.7',
                                                    fontFamily: 'Poppins, sans-serif'
                                                } }), _jsx(Text, { style: { fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '8px' }, children: "This narrative appears first in the appraisal. Describe the overall design, materials, craftsmanship, and aesthetic." })] }), _jsx("div", { style: { height: '2px', background: 'var(--border-color)', margin: '32px 0' } })] })), _jsxs("div", { style: { marginBottom: '32px' }, children: [mode === 'test' && (_jsx("div", { style: { marginBottom: '12px', padding: '12px', background: 'rgba(16, 185, 129, 0.1)', border: '1px solid #10b981' }, children: _jsxs(Text, { style: { fontSize: '12px', color: 'var(--text-secondary)', marginBottom: 0 }, children: [_jsx("strong", { children: "Step 2 of 3:" }), " Basic jewelry classification"] }) })), _jsx(Text, { style: { fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: 'var(--text-primary)' }, children: "Basic Classification" }), _jsxs(FormGrid, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { children: "Jewelry Type *" }), _jsx(Input, { name: "jewelryType", value: formData.jewelryType, onChange: handleInputChange, placeholder: "e.g., Engagement Ring", className: validationErrors.some(e => e.includes('type')) ? 'error' : '' })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Condition" }), _jsx(Input, { name: "condition", value: formData.condition, onChange: handleInputChange, placeholder: "e.g., Excellent" })] })] })] }), _jsxs("div", { style: { marginBottom: '32px' }, children: [_jsx(Text, { style: { fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: 'var(--text-primary)' }, children: "Metal Composition" }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Complete Metal Description *" }), _jsx(Input, { name: "metalType", value: formData.metalType, onChange: handleInputChange, placeholder: mode === 'test'
                                                    ? "Example: Platinum (950) four-prong head with 18-karat yellow gold high-polish shank"
                                                    : "e.g., Platinum, 18K Yellow Gold", className: validationErrors.some(e => e.includes('Metal type')) ? 'error' : '' }), _jsx(Text, { style: { fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }, children: "Describe all metals including specific parts (head, shank, prongs) and finishes" })] })] }), _jsxs("div", { style: { marginBottom: '32px' }, children: [_jsxs(Text, { style: { fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: 'var(--text-primary)' }, children: [_jsx(FaRing, { style: { marginRight: '8px' } }), "Setting & Craftsmanship Details"] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Setting Description" }), _jsx(Textarea, { name: "settingDescription", value: formData.settingDescription, onChange: handleInputChange, placeholder: "e.g., Classic four-prong solitaire setting, cathedral shoulders, knife-edge band with high-polish finish...", rows: 3 }), _jsx(Text, { style: { fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }, children: "Describe prong configuration, band style, design elements" })] })] }), mode === 'test' && (_jsxs(CustomFieldsSection, { children: [_jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }, children: [_jsx(Text, { style: { fontSize: '13px', fontWeight: 600, marginBottom: 0 }, children: "Additional Specifications (Optional Custom Fields)" }), _jsxs(Button, { variant: "secondary", onClick: () => handleAddCustomField(2), style: { padding: '6px 12px', fontSize: '12px' }, children: [_jsx(FaPlus, {}), " Add Custom Field"] })] }), customFieldsStep2.length === 0 ? (_jsx(Text, { style: { fontSize: '12px', color: 'var(--text-tertiary)', textAlign: 'center', marginBottom: 0 }, children: "Add custom key-value pairs (e.g., \"Metal Purity: 950 Platinum\", \"Finish: High-polish\")" })) : (customFieldsStep2.map((field) => (_jsxs(CustomFieldItem, { children: [_jsx(CustomFieldInput, { type: "text", placeholder: "Field name", value: field.fieldName, onChange: (e) => handleCustomFieldChange(2, field.id, 'fieldName', e.target.value) }), _jsx(CustomFieldInput, { type: "text", placeholder: "Value", value: field.fieldValue, onChange: (e) => handleCustomFieldChange(2, field.id, 'fieldValue', e.target.value) }), _jsx(Button, { variant: "secondary", onClick: () => handleRemoveCustomField(2, field.id), style: { padding: '6px 12px', fontSize: '12px' }, children: _jsx(FaTrash, {}) })] }, field.id))))] }))] })), currentStep === 3 && (_jsxs("div", { children: [_jsxs(SectionTitle, { style: { marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }, children: [_jsxs("span", { children: ["Gemstone Details (", gemstones.length, ")"] }), _jsxs(Button, { variant: "secondary", onClick: handleAddGemstone, children: [_jsx(FaPlus, {}), " Add Gemstone"] })] }), gemstones.length === 0 ? (_jsxs("div", { style: { textAlign: 'center', padding: '48px', color: 'var(--text-tertiary)' }, children: [_jsx(FaGem, { style: { fontSize: '48px', marginBottom: '16px', opacity: 0.3 } }), _jsx(Text, { children: "No gemstones added yet. Click \"Add Gemstone\" to include stones in this appraisal." }), _jsx(Text, { style: { fontSize: '13px', marginTop: '8px' }, children: "Optional: Skip if appraising plain metal jewelry" })] })) : (_jsx(GemstoneList, { children: gemstones.map((gem, index) => (_jsxs(GemstoneItem, { "$editing": editingGemId === gem.id, children: [_jsxs("div", { className: "gemstone-header", children: [_jsxs("h4", { children: [_jsx(FaGem, {}), " Gemstone ", index + 1] }), _jsxs("div", { className: "actions", children: [_jsxs(Button, { variant: "secondary", onClick: () => handleEditGemstone(gem.id), children: [_jsx(FaEdit, {}), " ", editingGemId === gem.id ? 'Save' : 'Edit'] }), _jsx(Button, { variant: "secondary", onClick: () => handleRemoveGemstone(gem.id), children: _jsx(FaTrash, {}) })] })] }), _jsxs("div", { className: "gemstone-details", children: [_jsxs("div", { className: "detail", children: [_jsx("div", { className: "label", children: "Species" }), editingGemId === gem.id ? (_jsx("input", { type: "text", value: gem.species, onChange: (e) => handleGemstoneChange(gem.id, 'species', e.target.value), placeholder: "e.g., Diamond, Ruby, Sapphire..." })) : (_jsx("div", { className: "value", children: gem.species }))] }), _jsxs("div", { className: "detail", children: [_jsx("div", { className: "label", children: "Cut" }), editingGemId === gem.id ? (_jsx("input", { type: "text", value: gem.cut, onChange: (e) => handleGemstoneChange(gem.id, 'cut', e.target.value) })) : (_jsx("div", { className: "value", children: gem.cut }))] }), _jsxs("div", { className: "detail", children: [_jsx("div", { className: "label", children: "Carat" }), editingGemId === gem.id ? (_jsx("input", { type: "text", value: gem.carat, onChange: (e) => handleGemstoneChange(gem.id, 'carat', e.target.value) })) : (_jsxs("div", { className: "value", children: [gem.carat, "ct"] }))] }), _jsxs("div", { className: "detail", children: [_jsx("div", { className: "label", children: "Color" }), editingGemId === gem.id ? (_jsx("input", { type: "text", value: gem.color, onChange: (e) => handleGemstoneChange(gem.id, 'color', e.target.value) })) : (_jsx("div", { className: "value", children: gem.color }))] }), _jsxs("div", { className: "detail", children: [_jsx("div", { className: "label", children: "Clarity" }), editingGemId === gem.id ? (_jsx("input", { type: "text", value: gem.clarity, onChange: (e) => handleGemstoneChange(gem.id, 'clarity', e.target.value) })) : (_jsx("div", { className: "value", children: gem.clarity }))] }), _jsxs("div", { className: "detail", children: [_jsx("div", { className: "label", children: "Value (USD)" }), editingGemId === gem.id ? (_jsx("input", { type: "number", value: gem.value, onChange: (e) => handleGemstoneChange(gem.id, 'value', e.target.value) })) : (_jsxs("div", { className: "value", children: ["$", gem.value] }))] }), mode === 'test' && editingGemId === gem.id && (_jsxs(_Fragment, { children: [_jsx("div", { className: "detail", style: { gridColumn: '1 / -1', marginTop: '16px', borderTop: '1px solid var(--border-color)', paddingTop: '16px' }, children: _jsx(Text, { style: { fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '12px' }, children: "GIA Certificate Information (Optional)" }) }), _jsxs("div", { className: "detail", children: [_jsx("div", { className: "label", children: "GIA Report #" }), _jsx("input", { type: "text", value: gem.giaReportNumber || '', onChange: (e) => handleGemstoneChange(gem.id, 'giaReportNumber', e.target.value), placeholder: "e.g., 7518751321" })] }), _jsxs("div", { className: "detail", children: [_jsx("div", { className: "label", children: "Measurements (mm)" }), _jsx("input", { type: "text", value: gem.measurements || '', onChange: (e) => handleGemstoneChange(gem.id, 'measurements', e.target.value), placeholder: "e.g., 10.14 \u00D7 7.22 \u00D7 4.33" })] }), _jsxs("div", { className: "detail", children: [_jsx("div", { className: "label", children: "Depth %" }), _jsx("input", { type: "text", value: gem.depthPercentage || '', onChange: (e) => handleGemstoneChange(gem.id, 'depthPercentage', e.target.value), placeholder: "e.g., 60.0" })] }), _jsxs("div", { className: "detail", children: [_jsx("div", { className: "label", children: "Table %" }), _jsx("input", { type: "text", value: gem.tablePercentage || '', onChange: (e) => handleGemstoneChange(gem.id, 'tablePercentage', e.target.value), placeholder: "e.g., 63" })] }), _jsxs("div", { className: "detail", children: [_jsx("div", { className: "label", children: "L/W Ratio" }), _jsx("input", { type: "text", value: gem.lengthToWidthRatio || '', onChange: (e) => handleGemstoneChange(gem.id, 'lengthToWidthRatio', e.target.value), placeholder: "e.g., 1.40" })] }), _jsxs("div", { className: "detail", children: [_jsx("div", { className: "label", children: "Girdle" }), _jsx("input", { type: "text", value: gem.girdle || '', onChange: (e) => handleGemstoneChange(gem.id, 'girdle', e.target.value), placeholder: "e.g., THN-VTK" })] }), _jsxs("div", { className: "detail", children: [_jsx("div", { className: "label", children: "Culet" }), _jsx("input", { type: "text", value: gem.culet || '', onChange: (e) => handleGemstoneChange(gem.id, 'culet', e.target.value), placeholder: "e.g., None" })] }), _jsxs("div", { className: "detail", children: [_jsx("div", { className: "label", children: "Polish" }), _jsx("input", { type: "text", value: gem.polish || '', onChange: (e) => handleGemstoneChange(gem.id, 'polish', e.target.value), placeholder: "e.g., EX" })] }), _jsxs("div", { className: "detail", children: [_jsx("div", { className: "label", children: "Symmetry" }), _jsx("input", { type: "text", value: gem.symmetry || '', onChange: (e) => handleGemstoneChange(gem.id, 'symmetry', e.target.value), placeholder: "e.g., EX" })] }), _jsxs("div", { className: "detail", children: [_jsx("div", { className: "label", children: "Fluorescence" }), _jsx("input", { type: "text", value: gem.fluorescence || '', onChange: (e) => handleGemstoneChange(gem.id, 'fluorescence', e.target.value), placeholder: "e.g., MED" })] })] }))] })] }, gem.id))) })), _jsx("div", { style: { marginTop: '24px', padding: '16px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }, children: _jsx(Text, { style: { fontSize: '13px', color: 'var(--text-secondary)' }, children: "Tip: Click \"Edit\" to modify gemstone details. Add all gemstones in the piece. For jewelry with multiple smaller stones (pave, halo), you can group them by total carat weight." }) })] })), currentStep === 4 && (_jsxs("div", { children: [_jsx(SectionTitle, { style: { marginBottom: '24px' }, children: "Photos & Documentation" }), _jsx("input", { ref: fileInputRef, type: "file", accept: "image/*", style: { display: 'none' }, onChange: handleFileSelect }), _jsxs(ImageUploadArea, { onClick: handlePhotoUpload, style: { opacity: uploadingPhoto ? 0.6 : 1, cursor: uploadingPhoto ? 'wait' : 'pointer' }, children: [_jsx(FaImage, {}), uploadingPhoto ? (_jsxs(_Fragment, { children: [_jsx(Text, { style: { marginBottom: '8px' }, children: "Uploading photo..." }), _jsx(Text, { style: { fontSize: '13px', color: 'var(--text-tertiary)' }, children: "Please wait while your image is being uploaded to secure storage" })] })) : (_jsxs(_Fragment, { children: [_jsx(Text, { style: { marginBottom: '8px' }, children: mode === 'test' ? 'Click to upload photos to blob storage' : 'Click to add photos (Mock Upload)' }), _jsx(Text, { style: { fontSize: '13px', color: 'var(--text-tertiary)' }, children: mode === 'test'
                                                    ? 'Upload images of the jewelry piece (JPG, PNG, max 5MB each)'
                                                    : 'Upload images of the jewelry piece from multiple angles' })] }))] }), photos.length > 0 && (_jsx(ImagePreviewGrid, { children: photos.map((photo, index) => (_jsxs(ImagePreview, { children: [_jsx("img", { src: photo, alt: `Jewelry photo ${index + 1}` }), _jsx("button", { className: "remove-btn", onClick: (e) => {
                                                e.stopPropagation();
                                                handleRemovePhoto(index);
                                            }, children: _jsx(FaTrash, {}) })] }, index))) })), _jsx("div", { style: { marginTop: '24px', padding: '16px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }, children: _jsx(Text, { style: { fontSize: '13px', color: 'var(--text-secondary)' }, children: "Best Practices: Include photos from top, side, and profile views. Include close-ups of any unique features or hallmarks." }) })] })), currentStep === 5 && (_jsxs("div", { children: [_jsx(SectionTitle, { style: { marginBottom: '24px' }, children: "Review & Valuation" }), _jsx(SummaryCard, { children: _jsxs("div", { className: "summary-grid", children: [_jsxs("div", { className: "summary-item", children: [_jsx("div", { className: "label", children: "Customer" }), _jsx("div", { className: "value", children: formData.customerName || 'Not specified' })] }), _jsxs("div", { className: "summary-item", children: [_jsx("div", { className: "label", children: "Jewelry Type" }), _jsx("div", { className: "value", children: formData.jewelryType || 'Not specified' })] }), _jsxs("div", { className: "summary-item", children: [_jsx("div", { className: "label", children: "Metal" }), _jsx("div", { className: "value", children: formData.metalType || 'Not specified' })] }), _jsxs("div", { className: "summary-item", children: [_jsx("div", { className: "label", children: "Gemstones" }), _jsxs("div", { className: "value", children: [gemstones.length, " stone(s)"] })] }), _jsxs("div", { className: "summary-item", children: [_jsx("div", { className: "label", children: "Photos" }), _jsxs("div", { className: "value", children: [photos.length, " image(s)"] })] })] }) }), _jsxs("div", { style: { marginBottom: '32px' }, children: [_jsx(Label, { style: { marginBottom: '16px', display: 'block' }, children: "Select Template Style" }), _jsxs(TemplateSelector, { children: [_jsxs(TemplateCard, { "$selected": formData.template === 'classic', onClick: () => setFormData(prev => ({ ...prev, template: 'classic' })), children: [_jsx("div", { className: "template-icon", children: _jsx(FaFileAlt, {}) }), _jsx("div", { className: "template-name", children: "Classic" }), _jsx("div", { className: "template-desc", children: "Traditional format" })] }), _jsxs(TemplateCard, { "$selected": formData.template === 'modern', onClick: () => setFormData(prev => ({ ...prev, template: 'modern' })), children: [_jsx("div", { className: "template-icon", children: _jsx(FaBolt, {}) }), _jsx("div", { className: "template-name", children: "Modern" }), _jsx("div", { className: "template-desc", children: "Clean & minimal" })] }), _jsxs(TemplateCard, { "$selected": formData.template === 'detailed', onClick: () => setFormData(prev => ({ ...prev, template: 'detailed' })), children: [_jsx("div", { className: "template-icon", children: _jsx(FaGem, {}) }), _jsx("div", { className: "template-name", children: "Detailed" }), _jsx("div", { className: "template-desc", children: "Comprehensive report" })] })] })] }), mode === 'mock' && (_jsxs("div", { style: { marginBottom: '32px' }, children: [_jsx(Label, { style: { marginBottom: '16px', display: 'block' }, children: "Appraiser Signature (Mock)" }), _jsxs(SignaturePad, { children: [_jsx("div", { className: "sig-canvas", onClick: () => setSignatureDrawn(!signatureDrawn), children: signatureDrawn ? (_jsx("div", { style: { fontFamily: 'Brush Script MT, cursive', fontSize: '32px', color: '#000' }, children: "John Smith" })) : ('Click to sign') }), _jsx("div", { className: "sig-actions", children: _jsxs(Button, { variant: "secondary", onClick: () => setSignatureDrawn(false), style: { fontSize: '12px', padding: '6px 12px' }, children: [_jsx(FaTrash, {}), " Clear"] }) })] })] })), mode === 'test' && (_jsxs("div", { style: { marginBottom: '32px' }, children: [_jsx(Label, { style: { marginBottom: '16px', display: 'block' }, children: "Dual Appraiser Signatures" }), _jsxs("div", { style: {
                                            padding: '20px',
                                            background: '#fff',
                                            border: '2px solid var(--border-color)',
                                            textAlign: 'center'
                                        }, children: [_jsx("img", { src: DUAL_SIGNATURE_IMAGE_URL, alt: "Dual Appraiser Signatures", style: {
                                                    maxWidth: '100%',
                                                    height: 'auto',
                                                    display: 'block',
                                                    margin: '0 auto'
                                                } }), _jsx(Text, { style: { fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '12px', marginBottom: 0 }, children: "Official signatures: Aneesh K. Jain (GIA, Graduate Diamonds) & Michael Gold (GIA, Graduate Gemologist)" })] })] })), mode === 'test' && (_jsx(_Fragment, { children: _jsxs("div", { style: { marginBottom: '32px', padding: '24px', background: 'var(--bg-tertiary)', border: '2px solid #10b981' }, children: [_jsx(SectionTitle, { style: { fontSize: '16px', marginBottom: '16px', color: '#10b981' }, children: "Professional Appraisal Information" }), _jsx(Text, { style: { fontSize: '13px', marginBottom: '24px' }, children: "Additional fields required for insurance and professional appraisals" }), _jsx("div", { style: { marginBottom: '24px', padding: '16px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }, children: _jsxs("label", { style: { display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }, children: [_jsx("input", { type: "checkbox", checked: formData.showRoyalGemsBranding, onChange: (e) => setFormData(prev => ({ ...prev, showRoyalGemsBranding: e.target.checked })), style: { width: '18px', height: '18px', cursor: 'pointer' } }), _jsxs("div", { children: [_jsx(Text, { style: { fontSize: '13px', fontWeight: 600, marginBottom: '4px', color: 'var(--text-primary)' }, children: "Include \"Royal Gems\" Branding" }), _jsx(Text, { style: { fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: 0 }, children: "Check this box for business/official appraisals. Uncheck for personal appraisals (company info only)." })] })] }) }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Complete Item Description (Narrative) *" }), _jsx("textarea", { name: "itemDescription", value: formData.itemDescription, onChange: handleInputChange, placeholder: "e.g., A finely handcrafted ring featuring a 2.01 carat natural oval brilliant-cut diamond, elegantly mounted in a platinum (950) four-prong head with an 18-karat yellow gold high-polish shank...", rows: 4, style: {
                                                        width: '100%',
                                                        padding: '12px',
                                                        background: 'var(--bg-secondary)',
                                                        border: '1px solid var(--border-color)',
                                                        color: 'var(--text-primary)',
                                                        fontSize: '14px',
                                                        lineHeight: '1.6'
                                                    } })] }), _jsxs(FormGrid, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { children: "Diamond Replacement Value (USD) *" }), _jsx(Input, { name: "diamondRingValue", value: formData.diamondRingValue, onChange: handleInputChange, placeholder: "e.g., 26000", type: "number" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Ring Replacement Value (USD)" }), _jsx(Input, { name: "goldRingValue", value: formData.goldRingValue, onChange: handleInputChange, placeholder: "e.g., 2000", type: "number" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Metal Price Reference (Optional)" }), _jsx(Input, { name: "goldPriceReference", value: formData.goldPriceReference, onChange: handleInputChange, placeholder: "e.g., GOLD: $4,366.70 USD" })] })] }), _jsxs("div", { style: { marginTop: '24px', paddingTop: '24px', borderTop: '1px solid var(--border-color)' }, children: [_jsx(Label, { style: { marginBottom: '16px', display: 'block', fontSize: '14px' }, children: "Company Information" }), _jsxs(FormGrid, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { children: "Company Name" }), _jsx(Input, { name: "companyName", value: formData.companyName, onChange: handleInputChange, placeholder: "Aneesh Jain, LLC." })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Company Phone" }), _jsx(Input, { name: "companyPhone", value: formData.companyPhone, onChange: handleInputChange, placeholder: "513-349-8816" })] }), _jsxs(FormGroup, { style: { gridColumn: '1 / -1' }, children: [_jsx(Label, { children: "Company Address" }), _jsx(Textarea, { name: "companyAddress", value: formData.companyAddress, onChange: handleInputChange, placeholder: "525 Vine St, 23rd floor\nCincinnati, OH 45202", rows: 2 })] })] })] }), _jsxs("div", { style: { marginTop: '24px', paddingTop: '24px', borderTop: '1px solid var(--border-color)' }, children: [_jsx(Label, { style: { marginBottom: '16px', display: 'block', fontSize: '14px' }, children: "Appraisers (Dual Signature)" }), _jsxs(FormGrid, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { children: "Appraiser 1 Name" }), _jsx(Input, { name: "appraiser1Name", value: formData.appraiser1Name, onChange: handleInputChange, placeholder: "Aneesh K. Jain" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Appraiser 1 Title" }), _jsx(Input, { name: "appraiser1Title", value: formData.appraiser1Title, onChange: handleInputChange, placeholder: "GIA, Graduate Diamonds" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Appraiser 2 Name" }), _jsx(Input, { name: "appraiser2Name", value: formData.appraiser2Name, onChange: handleInputChange, placeholder: "Michael Gold" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Appraiser 2 Title" }), _jsx(Input, { name: "appraiser2Title", value: formData.appraiser2Title, onChange: handleInputChange, placeholder: "GIA, Graduate Gemologist" })] })] })] })] }) })), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Total Appraised Value (USD) *" }), _jsx(Input, { name: "totalValue", value: formData.totalValue, onChange: handleInputChange, placeholder: `Suggested: $${autoCalculatedValue.toLocaleString()}`, type: "number", style: { fontSize: '20px', fontWeight: 'bold' }, className: validationErrors.some(e => e.includes('value')) ? 'error' : '' }), _jsx(Text, { style: { fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px' }, children: mode === 'test'
                                            ? 'Sum of Diamond Ring Value + Gold Ring Value'
                                            : `Auto-calculated suggestion: $${autoCalculatedValue.toLocaleString()} (based on gemstones + metal + labor)` })] }), _jsxs("div", { style: { marginTop: '32px', padding: '24px', background: 'rgba(16, 185, 129, 0.1)', border: '2px solid #10b981' }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }, children: [_jsx(FaCheckCircle, { style: { color: '#10b981', fontSize: '24px' } }), _jsx(Text, { style: { fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }, children: "Ready to Generate Appraisal" })] }), _jsx(Text, { style: { fontSize: '13px', color: 'var(--text-secondary)' }, children: "Review the information above. Click \"Preview PDF\" to see the appraisal document, or \"Save Draft\" to resume later." })] })] })), _jsxs(ButtonGroup, { children: [_jsxs("div", { style: { display: 'flex', gap: '12px' }, children: [currentStep > 1 && (_jsx(Button, { variant: "secondary", onClick: handlePrevStep, children: "Back" })), currentStep === 5 && (_jsxs(Button, { variant: "secondary", onClick: handleSaveDraft, children: [_jsx(FaSave, {}), " Save Draft"] }))] }), _jsx("div", { style: { display: 'flex', gap: '12px' }, children: currentStep < 5 ? (_jsx(Button, { onClick: handleNextStep, children: "Next Step" })) : (_jsxs(_Fragment, { children: [mode === 'test' && (_jsxs(_Fragment, { children: [_jsxs(Button, { variant: "secondary", onClick: handleGenerateAppraisal, children: [_jsx(FaEye, {}), " Preview PDF"] }), _jsxs(Button, { variant: "secondary", onClick: () => saveAppraisal('draft'), children: [_jsx(FaSave, {}), " Save Draft"] }), _jsxs(Button, { onClick: () => saveAppraisal('completed'), style: { background: '#10b981', borderColor: '#10b981' }, children: [_jsx(FaCheckCircle, {}), " Complete Appraisal"] })] })), mode === 'mock' && (_jsxs(_Fragment, { children: [_jsxs(Button, { variant: "secondary", onClick: handleSendEmail, children: [_jsx(FaEnvelope, {}), " Email Preview"] }), _jsxs(Button, { onClick: handleGenerateAppraisal, children: [_jsx(FaEye, {}), " Preview PDF"] })] }))] })) })] })] })), activeTab === 'history' && (_jsxs("div", { children: [_jsxs(FilterBar, { id: "tour-filter-bar", children: [_jsx(Input, { placeholder: "Search by customer name, appraisal number, or jewelry type...", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), style: { flex: 1, minWidth: '300px' } }), _jsxs(Select, { value: statusFilter, onChange: (e) => setStatusFilter(e.target.value), style: { width: '200px' }, children: [_jsx("option", { value: "all", children: "All Statuses" }), _jsx("option", { value: "completed", children: "Completed" }), _jsx("option", { value: "sent", children: "Sent to Customer" }), _jsx("option", { value: "draft", children: "Draft" }), _jsx("option", { value: "archived", children: "Archived" })] }), _jsxs(Select, { value: dateFilter, onChange: (e) => setDateFilter(e.target.value), style: { width: '200px' }, children: [_jsx("option", { value: "all", children: "All Time" }), _jsx("option", { value: "7days", children: "Last 7 Days" }), _jsx("option", { value: "30days", children: "Last 30 Days" }), _jsx("option", { value: "90days", children: "Last 90 Days" })] }), _jsxs(Select, { value: sortBy, onChange: (e) => setSortBy(e.target.value), style: { width: '200px' }, children: [_jsx("option", { value: "date", children: "Sort by Date" }), _jsx("option", { value: "value", children: "Sort by Value" }), _jsx("option", { value: "customer", children: "Sort by Customer" })] }), _jsxs(Button, { variant: "secondary", onClick: handleExportCSV, children: [_jsx(FaFileCsv, {}), " Export CSV"] })] }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }, children: [_jsxs(Text, { style: { marginBottom: 0, color: 'var(--text-secondary)' }, children: ["Showing ", filteredAppraisals.length, " of ", mode === 'test' ? realAppraisals.length : mockAppraisals.length, " appraisals"] }), isAdmin && mode === 'test' && (_jsxs(Badge, { style: { background: '#3b82f6', color: '#fff', fontSize: '11px', padding: '4px 12px' }, children: [_jsx(FaUser, { style: { marginRight: '4px' } }), "Admin View: All Users"] }))] }), _jsx(AppraisalsList, { children: filteredAppraisals.map((appraisal, index) => (_jsxs(AppraisalCard, { id: index === 0 ? 'tour-first-appraisal' : undefined, children: [_jsxs("div", { className: "appraisal-header", children: [_jsxs("div", { children: [_jsxs("h3", { children: [getAppraisalField(appraisal, 'jewelryType').includes('Ring') && _jsx(FaRing, {}), getAppraisalField(appraisal, 'jewelryType').includes('Bracelet') && _jsx(FaLink, {}), getAppraisalField(appraisal, 'jewelryType').includes('Necklace') && _jsx(FaGem, {}), getAppraisalField(appraisal, 'jewelryType').includes('Earring') && _jsx(FaGem, {}), getAppraisalField(appraisal, 'jewelryType')] }), _jsx("div", { className: "customer-name", children: appraisal.customerName })] }), _jsx(Badge, { style: { background: getStatusColor(appraisal.status), color: appraisal.status === 'draft' ? '#000' : '#fff' }, children: getStatusLabel(appraisal.status) })] }), _jsxs("div", { className: "appraisal-details", children: [_jsxs("div", { className: "detail-item", children: [_jsx("div", { className: "label", children: "Appraisal Number" }), _jsx("div", { className: "value", children: appraisal.appraisalNumber })] }), isAdmin && mode === 'test' && appraisal.userId && (_jsxs("div", { className: "detail-item", children: [_jsx("div", { className: "label", children: "Created By" }), _jsxs("div", { className: "value", style: { fontSize: '11px', color: '#3b82f6' }, children: [_jsx(FaUser, { style: { marginRight: '4px' } }), appraisal.userId.substring(0, 20), "..."] })] })), _jsxs("div", { className: "detail-item", children: [_jsx("div", { className: "label", children: "Date Created" }), _jsx("div", { className: "value", children: new Date(getAppraisalField(appraisal, 'date')).toLocaleDateString() })] }), _jsxs("div", { className: "detail-item", children: [_jsx("div", { className: "label", children: "Metal" }), _jsx("div", { className: "value", children: getAppraisalField(appraisal, 'metalType') })] }), _jsxs("div", { className: "detail-item", children: [_jsx("div", { className: "label", children: "Gemstones" }), _jsxs("div", { className: "value", children: [(appraisal.gemstones || []).length, " stone(s)"] })] }), _jsxs("div", { className: "detail-item", children: [_jsx("div", { className: "label", children: "Total Value" }), _jsxs("div", { className: "value", style: { color: '#10b981' }, children: ["$", parseInt(getAppraisalField(appraisal, 'totalValue') || '0').toLocaleString()] })] }), _jsxs("div", { className: "detail-item", children: [_jsx("div", { className: "label", children: "Template" }), _jsx("div", { className: "value", style: { textTransform: 'capitalize' }, children: getAppraisalField(appraisal, 'template') || 'N/A' })] })] }), _jsxs("div", { className: "appraisal-actions", children: [_jsxs(Button, { variant: "secondary", onClick: () => handleViewAppraisal(appraisal), children: [_jsx(FaEye, {}), " View PDF"] }), _jsxs(Button, { variant: "secondary", onClick: () => handleEditAppraisal(appraisal), children: [_jsx(FaEdit, {}), " Edit"] }), _jsxs(Button, { variant: "secondary", onClick: () => handleSendEmail(), children: [_jsx(FaEnvelope, {}), " Email"] }), _jsxs(Button, { variant: "secondary", onClick: () => handleDuplicateAppraisal(appraisal), children: [_jsx(FaCopy, {}), " Duplicate"] }), _jsxs(Button, { variant: "secondary", style: { marginLeft: 'auto' }, onClick: () => handleDeleteAppraisal(appraisal), children: [_jsx(FaTrash, {}), " Delete"] })] })] }, appraisal.id))) })] })), showPDFPreview && (() => {
                const previewFormData = selectedAppraisal?.formData || formData;
                return (_jsx(BaseModal, { isOpen: true, onClose: () => {
                        setShowPDFPreview(false);
                        setSelectedAppraisal(null);
                    }, title: `Appraisal Document Preview - ${(previewFormData.template || 'classic').toUpperCase()} Template`, maxWidth: "1000px", footer: _jsxs("div", { style: { display: 'flex', gap: '12px', justifyContent: 'flex-end' }, children: [_jsx(Button, { variant: "secondary", onClick: () => {
                                    setShowPDFPreview(false);
                                    setSelectedAppraisal(null);
                                }, children: "Close" }), _jsxs(Button, { variant: "secondary", onClick: handleSendEmail, children: [_jsx(FaEnvelope, {}), " Email to Customer"] }), _jsxs(Button, { onClick: handleDownloadPDF, disabled: downloadingPDF, children: [_jsx(FaDownload, {}), " ", downloadingPDF ? 'Generating PDF...' : 'Download PDF'] })] }), children: _jsxs(PDFPreview, { ref: pdfContentRef, children: [_jsx("div", { style: { textAlign: 'center', marginBottom: '24px' }, children: _jsx("img", { src: DIAMOND_LOGO_URL, alt: "Diamond Logo", style: {
                                        width: '80px',
                                        height: 'auto',
                                        display: 'block',
                                        margin: '0 auto'
                                    } }) }), _jsxs("div", { className: "pdf-header", children: [_jsx("div", { className: "company-name", children: mode === 'test' ? previewFormData.companyName : 'ROYAL GEMS, INC.' }), _jsx("div", { className: "company-info", children: mode === 'test' ? (_jsxs(_Fragment, { children: [previewFormData.companyAddress?.split('\n').map((line, i) => (_jsxs("span", { children: [line, _jsx("br", {})] }, i))), "Phone: ", previewFormData.companyPhone] })) : (_jsxs(_Fragment, { children: ["Certified Gemologists & Appraisers", _jsx("br", {}), "123 Diamond District, New York, NY 10036", _jsx("br", {}), "Phone: (555) 123-4567 | Email: appraisals@royalgems.com"] })) }), _jsx("h1", { children: "APPRAISAL FOR INSURANCE" }), _jsxs("div", { style: { fontSize: '14px', color: '#666', marginTop: '16px' }, children: ["Appraisal Number: ", selectedAppraisal?.appraisalNumber || getPreviewAppraisalNumber(), _jsx("br", {}), "Date of Appraisal: ", selectedAppraisal?.date ? new Date(selectedAppraisal.date).toLocaleDateString() : new Date().toLocaleDateString(), _jsx("br", {}), mode === 'test' && (_jsxs(_Fragment, { children: ["The item described below is the property of:", _jsx("br", {}), _jsx("strong", { children: selectedAppraisal?.customerName || previewFormData.customerName }), _jsx("br", {}), (selectedAppraisal?.customerAddress || previewFormData.customerAddress) && (_jsx(_Fragment, { children: (selectedAppraisal?.customerAddress || previewFormData.customerAddress)?.split('\n').map((line, i) => (_jsxs("span", { children: [line, _jsx("br", {})] }, i))) }))] }))] })] }), mode === 'test' && previewFormData.itemDescription && (_jsxs("div", { className: "pdf-section", children: [_jsx("h2", { children: "Complete Item Description" }), _jsx("p", { style: { lineHeight: '1.8', textAlign: 'justify', fontSize: '14px', color: '#333' }, children: previewFormData.itemDescription })] })), _jsxs("div", { className: "pdf-section", children: [_jsx("h2", { children: "Customer Information" }), _jsxs("div", { className: "info-grid", children: [_jsxs("div", { className: "info-row", children: [_jsx("span", { className: "label", children: "Name:" }), _jsx("span", { className: "value", children: selectedAppraisal?.customerName || previewFormData.customerName })] }), _jsxs("div", { className: "info-row", children: [_jsx("span", { className: "label", children: "Email:" }), _jsx("span", { className: "value", children: selectedAppraisal?.customerEmail || previewFormData.customerEmail })] }), _jsxs("div", { className: "info-row", children: [_jsx("span", { className: "label", children: "Phone:" }), _jsx("span", { className: "value", children: selectedAppraisal?.customerPhone || previewFormData.customerPhone })] }), _jsxs("div", { className: "info-row", children: [_jsx("span", { className: "label", children: "Appraisal Date:" }), _jsx("span", { className: "value", children: selectedAppraisal?.date ? new Date(selectedAppraisal.date).toLocaleDateString() : new Date().toLocaleDateString() })] }), mode === 'test' && (selectedAppraisal?.customFieldsStep1 || customFieldsStep1).map((field) => (_jsxs("div", { className: "info-row", children: [_jsxs("span", { className: "label", children: [field.fieldName, ":"] }), _jsx("span", { className: "value", children: field.fieldValue })] }, field.id)))] })] }), _jsxs("div", { className: "pdf-section", children: [_jsx("h2", { children: "Item Description" }), _jsxs("div", { className: "info-grid", children: [_jsxs("div", { className: "info-row", children: [_jsx("span", { className: "label", children: "Type:" }), _jsx("span", { className: "value", children: previewFormData.jewelryType })] }), _jsxs("div", { className: "info-row", children: [_jsx("span", { className: "label", children: "Metal Type:" }), _jsx("span", { className: "value", children: previewFormData.metalType })] }), _jsxs("div", { className: "info-row", style: { gridColumn: '1 / -1' }, children: [_jsx("span", { className: "label", children: "Setting Description:" }), _jsx("span", { className: "value", children: previewFormData.settingDescription })] }), _jsxs("div", { className: "info-row", children: [_jsx("span", { className: "label", children: "Condition:" }), _jsx("span", { className: "value", children: previewFormData.condition })] }), mode === 'test' && (selectedAppraisal?.customFieldsStep2 || customFieldsStep2).map((field) => (_jsxs("div", { className: "info-row", children: [_jsxs("span", { className: "label", children: [field.fieldName, ":"] }), _jsx("span", { className: "value", children: field.fieldValue })] }, field.id)))] })] }), (selectedAppraisal?.gemstones.length ?? gemstones.length) > 0 && (_jsxs("div", { className: "pdf-section", children: [_jsx("h2", { children: "Gemstone Details" }), _jsxs("table", { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "#" }), _jsx("th", { children: "Species" }), _jsx("th", { children: "Cut" }), _jsx("th", { children: "Carat" }), _jsx("th", { children: "Color" }), _jsx("th", { children: "Clarity" }), _jsx("th", { children: "Value" })] }) }), _jsx("tbody", { children: (selectedAppraisal?.gemstones || gemstones).map((gem, index) => (_jsxs("tr", { children: [_jsx("td", { children: index + 1 }), _jsx("td", { children: gem.species }), _jsx("td", { children: gem.cut }), _jsxs("td", { children: [gem.carat, "ct"] }), _jsx("td", { children: gem.color }), _jsx("td", { children: gem.clarity }), _jsxs("td", { children: ["$", parseFloat(gem.value).toLocaleString()] })] }, index))) })] })] })), (selectedAppraisal?.photos.length ?? photos.length) > 0 && (_jsxs("div", { className: "pdf-section", children: [_jsx("h2", { children: "Item Photography" }), _jsx("div", { className: "pdf-photos", children: (selectedAppraisal?.photos || photos).slice(0, 6).map((photo, index) => (_jsx("img", { src: photo, alt: `Jewelry photo ${index + 1}` }, index))) })] })), mode === 'test' && (selectedAppraisal?.customFieldsStep5 || customFieldsStep5).length > 0 && (_jsx("div", { style: { marginTop: '24px', marginBottom: '16px' }, children: _jsx("div", { className: "info-grid", children: (selectedAppraisal?.customFieldsStep5 || customFieldsStep5).map((field) => (_jsxs("div", { className: "info-row", children: [_jsxs("span", { className: "label", children: [field.fieldName, ":"] }), _jsx("span", { className: "value", children: field.fieldValue })] }, field.id))) }) })), mode === 'test' && (previewFormData.diamondRingValue || previewFormData.goldRingValue || previewFormData.goldPriceReference) && (_jsxs("div", { className: "pdf-section", children: [_jsx("h2", { children: "Valuation Breakdown" }), _jsxs("div", { className: "info-grid", children: [previewFormData.diamondRingValue && (_jsxs("div", { className: "info-row", children: [_jsx("span", { className: "label", children: "Diamond Replacement Value:" }), _jsxs("span", { className: "value", children: ["$", parseInt(previewFormData.diamondRingValue).toLocaleString(), " USD"] })] })), previewFormData.goldRingValue && (_jsxs("div", { className: "info-row", children: [_jsx("span", { className: "label", children: "Ring Replacement Value:" }), _jsxs("span", { className: "value", children: ["$", parseInt(previewFormData.goldRingValue).toLocaleString(), " USD"] })] })), previewFormData.goldPriceReference && (_jsxs("div", { className: "info-row", children: [_jsx("span", { className: "label", children: "Metal Price Reference:" }), _jsx("span", { className: "value", children: previewFormData.goldPriceReference })] }))] })] })), _jsxs("div", { className: "pdf-total", children: ["TOTAL APPRAISED VALUE: $", parseInt(previewFormData.totalValue || '0').toLocaleString(), " USD"] }), _jsx("div", { className: "pdf-signature", children: mode === 'test' ? (
                                /* Real Dual Signature Image */
                                _jsxs("div", { style: { marginTop: '32px', marginBottom: '24px' }, children: [_jsx("div", { style: { textAlign: 'center', marginBottom: '16px' }, children: _jsx("img", { src: DUAL_SIGNATURE_IMAGE_URL, alt: "Dual Appraiser Signatures", style: {
                                                    width: 'auto',
                                                    maxWidth: '100%',
                                                    height: 'auto',
                                                    display: 'block',
                                                    margin: '0 auto',
                                                    objectFit: 'contain'
                                                } }) }), _jsx("div", { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', fontSize: '13px', color: '#000' }, children: _jsx("div", { children: _jsxs("div", { style: { fontWeight: 600 }, children: ["DATE: ", new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase()] }) }) })] })) : (
                                /* Mock Signature */
                                _jsxs(_Fragment, { children: [_jsxs("div", { className: "signature-box", children: [_jsx("div", { className: "signature-label", children: "Appraiser Signature:" }), _jsx("div", { className: "signature-line", style: { fontFamily: 'Brush Script MT, cursive', fontSize: '28px', display: 'flex', alignItems: 'center', paddingLeft: '8px' }, children: signatureDrawn && 'John Smith' }), _jsxs("div", { className: "signature-name", children: ["John Smith, GG (Graduate Gemologist)", _jsx("br", {}), "GIA License #12345678"] })] }), _jsxs("div", { className: "signature-box", children: [_jsx("div", { className: "signature-label", children: "Date:" }), _jsx("div", { className: "signature-line", style: { display: 'flex', alignItems: 'center', paddingLeft: '8px', fontSize: '16px' }, children: new Date().toLocaleDateString() })] })] })) })] }) }));
            })(), showEmailPreview && (_jsx(BaseModal, { isOpen: true, onClose: () => setShowEmailPreview(false), title: "Email Preview - Appraisal Delivery", maxWidth: "700px", footer: _jsxs("div", { style: { display: 'flex', gap: '12px', justifyContent: 'flex-end' }, children: [_jsx(Button, { variant: "secondary", onClick: () => setShowEmailPreview(false), children: "Close" }), _jsxs(Button, { onClick: handleSendViaGmail, disabled: downloadingPDF, children: [_jsx(FaEnvelope, {}), " ", downloadingPDF ? 'Preparing...' : 'Send Email'] })] }), children: _jsx("div", { style: { background: '#f5f5f5', padding: '32px', color: '#000' }, children: _jsxs("div", { style: { background: 'white', padding: '32px', border: '1px solid #ddd', fontFamily: 'Arial, sans-serif' }, children: [_jsx("div", { style: { textAlign: 'center', marginBottom: '24px' }, children: _jsx("img", { src: DIAMOND_LOGO_URL, alt: "Diamond Logo", style: {
                                        width: '60px',
                                        height: 'auto',
                                        display: 'block',
                                        margin: '0 auto'
                                    } }) }), _jsxs("div", { style: { textAlign: 'center', marginBottom: '32px', paddingBottom: '16px', borderBottom: '2px solid #000' }, children: [_jsx("h2", { style: { margin: '0 0 8px 0', color: '#000' }, children: mode === 'test' ? (formData.companyName || 'ANEESH JAIN, LLC.') : 'ROYAL GEMS, INC.' }), _jsx("p", { style: { margin: 0, fontSize: '14px', color: '#666' }, children: mode === 'test' ? 'Certified Gemologists & Appraisers' : 'Certified Gemologists & Appraisers' })] }), _jsxs("p", { style: { color: '#000', lineHeight: '1.6', marginBottom: '16px' }, children: ["Dear ", formData.customerName || 'Valued Customer', ","] }), _jsx("p", { style: { color: '#000', lineHeight: '1.6', marginBottom: '16px' }, children: mode === 'test' && formData.showRoyalGemsBranding ? (_jsx(_Fragment, { children: "Thank you for choosing Royal Gems for your jewelry appraisal needs. Please find attached your official jewelry appraisal document for insurance purposes." })) : mode === 'test' ? (_jsx(_Fragment, { children: "Thank you for your request. Please find attached your official jewelry appraisal document for insurance purposes." })) : (_jsx(_Fragment, { children: "Thank you for choosing Royal Gems for your jewelry appraisal needs. Please find attached your official jewelry appraisal document for insurance purposes." })) }), _jsxs("div", { style: { background: '#f9f9f9', padding: '20px', margin: '24px 0', border: '1px solid #ddd' }, children: [_jsx("p", { style: { margin: '0 0 8px 0', color: '#000', fontWeight: 'bold' }, children: "Appraisal Summary:" }), _jsxs("p", { style: { margin: '4px 0', color: '#000' }, children: [_jsx("strong", { children: "Item:" }), " ", formData.jewelryType] }), _jsxs("p", { style: { margin: '4px 0', color: '#000' }, children: [_jsx("strong", { children: "Appraisal Number:" }), " ", selectedAppraisal?.appraisalNumber || getPreviewAppraisalNumber()] }), _jsxs("p", { style: { margin: '4px 0', color: '#000' }, children: [_jsx("strong", { children: "Appraised Value:" }), " $", parseInt(formData.totalValue || '0').toLocaleString(), " USD"] }), _jsxs("p", { style: { margin: '4px 0', color: '#000' }, children: [_jsx("strong", { children: "Valid Until:" }), " ", new Date(new Date().setFullYear(new Date().getFullYear() + 2)).toLocaleDateString()] })] }), _jsx("p", { style: { color: '#000', lineHeight: '1.6', marginBottom: '16px' }, children: "Please provide this appraisal to your insurance provider for coverage purposes. We recommend updating your appraisal every 2-3 years to reflect current market conditions." }), _jsx("p", { style: { color: '#000', lineHeight: '1.6', marginBottom: '16px' }, children: "If you have any questions or need additional documentation, please don't hesitate to contact us." }), _jsxs("p", { style: { color: '#000', lineHeight: '1.6', marginTop: '32px' }, children: ["Best regards,", _jsx("br", {}), mode === 'test' ? (_jsxs(_Fragment, { children: [_jsx("strong", { children: formData.appraiser1Name }), _jsx("br", {}), formData.appraiser1Title, _jsx("br", {}), formData.showRoyalGemsBranding && (_jsxs(_Fragment, { children: ["Royal Gems / ", formData.companyName, _jsx("br", {})] })), !formData.showRoyalGemsBranding && (_jsxs(_Fragment, { children: [formData.companyName, _jsx("br", {})] })), formData.companyPhone] })) : (_jsxs(_Fragment, { children: [_jsx("strong", { children: "John Smith" }), _jsx("br", {}), "Graduate Gemologist (GG)", _jsx("br", {}), "Royal Gems, Inc.", _jsx("br", {}), "(555) 123-4567"] }))] }), _jsx("div", { style: { marginTop: '32px', padding: '16px', background: '#f0f0f0', border: '1px solid #ddd', fontSize: '11px', color: '#666', textAlign: 'center' }, children: "This email and any attachments are confidential and intended solely for the recipient." })] }) }) })), showSaveTemplateModal && (_jsx(BaseModal, { isOpen: showSaveTemplateModal, onClose: () => {
                    setShowSaveTemplateModal(false);
                    setTemplateName('');
                    setTemplateDescription('');
                }, title: "Save as Template", children: _jsxs("div", { style: { padding: '24px' }, children: [_jsx(Text, { style: { marginBottom: '24px', color: 'var(--text-secondary)' }, children: "Save the current appraisal configuration as a reusable template for similar jewelry pieces." }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Template Name *" }), _jsx(Input, { value: templateName, onChange: (e) => setTemplateName(e.target.value), placeholder: "e.g., Standard Diamond Ring, Platinum Engagement Ring..." })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Description (Optional)" }), _jsx(Input, { value: templateDescription, onChange: (e) => setTemplateDescription(e.target.value), placeholder: "e.g., Template for engagement rings with GIA certified diamonds" })] }), _jsxs("div", { style: { display: 'flex', gap: '12px', marginTop: '32px' }, children: [_jsx(Button, { variant: "secondary", onClick: () => {
                                        setShowSaveTemplateModal(false);
                                        setTemplateName('');
                                        setTemplateDescription('');
                                    }, children: "Cancel" }), _jsxs(Button, { onClick: saveTemplate, style: { background: '#10b981', borderColor: '#10b981' }, children: [_jsx(FaSave, {}), " Save Template"] })] })] }) }))] }));
};
export default AppraisalMaker;
